import React, { useState, useEffect, useCallback } from "react";
import Navbar from "./Navbar";
import moment from "moment";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import {
  Table,
  Card,
  Container,
  Row,
  Col,
  FloatingLabel,
  Spinner,
} from "react-bootstrap";
import PageTitle from "./components/PageTitle";
import { WeekHourGrid, WeekHourOverview, applyMergedSelection, entryRanges } from "./components/WeekHourGrid";
import { API_ENDPOINTS } from "./config/api";
import { Clock, ChartColumnStacked, ShieldCheck, TrendingUp, SlidersHorizontal, Hourglass, ChevronLeft, ChevronRight, Forward } from 'lucide-react';

const WorkHourLog = () => {
  const [weekDates, setWeekDates] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week
  const [logData, setLogData] = useState({});
  const [weekTotal, setWeekTotal] = useState("00:00");
  const [savedDaysInWeek, setSavedDaysInWeek] = useState(0);
  const [submittedWeeks, setSubmittedWeeks] = useState([]);
  const [unlockedWeeks, setUnlockedWeeks] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [useSlider, setUseSlider] = useState(() => {
    // Load preference from localStorage on component mount
    const saved = localStorage.getItem("workHourLogUseSlider");
    return saved ? JSON.parse(saved) : false;
  });
  const [useWeekGrid, setUseWeekGrid] = useState(() => {
    const saved = localStorage.getItem("workHourLogUseWeekGrid");
    return saved ? JSON.parse(saved) : false;
  });
  const [savingWeek, setSavingWeek] = useState(false);
  const [rotationOptions, setRotationOptions] = useState([]);
  // Mobile view states
  const [isMobile, setIsMobile] = useState(false);
  const [mobileCurrentDayIndex, setMobileCurrentDayIndex] = useState(0);
  const [mobileSaving, setMobileSaving] = useState(false);
  const currentWeekStart = moment()
    .add(weekOffset, "weeks")
    .startOf("week")
    .format("YYYY-MM-DD");
  const isSubmittedWeek = submittedWeeks.includes(currentWeekStart);
  const isUnlockedWeek = unlockedWeeks.includes(currentWeekStart);
  const isEditableWeek = !isSubmittedWeek || isUnlockedWeek;
  const [allLogs, setAllLogs] = useState([]); // Store all logs for the resident
  const [monthTotal, setMonthTotal] = useState("00:00");
  const [activeTab, setActiveTab] = useState("work");
  const [vacationRequests, setVacationRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState("Vacation");
  const [requestStart, setRequestStart] = useState("");
  const [requestEnd, setRequestEnd] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [vacationRequestError, setVacationRequestError] = useState("");
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [leaveAllotment, setLeaveAllotment] = useState({
    vacationDays: 20,
    sickDays: 10,
    usedVacationDays: 0,
    usedSickDays: 0,
  });
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(() => {
    const now = new Date();
    const year =
      now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
  });
  const [allVacationRequests, setAllVacationRequests] = useState([]); // Add this state for all vacation requests
  const [sliderInteraction, setSliderInteraction] = useState({});
  const [loading, setLoading] = useState(true);
  // View mode: 'week' or 'day'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("workHourLogViewMode") || "week";
  });
  // Selected day for day view
  const [selectedDay, setSelectedDay] = useState(null);
  const shouldShowTodayButton =
    !selectedDay || !selectedDay.isSame(moment(), "day");
  // Day entries structure: { [date]: [{ id, activity, startTime, endTime, breakTime, comments, isSaved }] }
  const [dayEntries, setDayEntries] = useState({});
  // Editing entry ID (null when not editing)
  const [editingEntryId, setEditingEntryId] = useState(null);
  // Selected date (YYYY-MM-DD) for week view sidebar details
  const [selectedWeekDate, setSelectedWeekDate] = useState(null);
  // Per‑day comments captured from the week view sidebar
  const [sidebarComments, setSidebarComments] = useState({});
  const [sidebarSaving, setSidebarSaving] = useState(false);

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hStr = "0", mStr = "0"] = timeStr.split(":");
    const hours = parseInt(hStr, 10);
    const minutes = parseInt(mStr, 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
    return hours * 60 + minutes;
  };

  const weekMinutes = parseTimeToMinutes(weekTotal);
  const weekHoursDecimal = weekMinutes / 60;
  const daysForAverage = savedDaysInWeek > 0 ? savedDaysInWeek : 0;
  const dailyAverageHours =
    daysForAverage > 0 && weekMinutes > 0 ? weekHoursDecimal / daysForAverage : 0;
  const weeklyPercentOf80 = (weekMinutes / (80 * 60)) * 100;
  const complianceRate = Math.min(
    Math.max((weekHoursDecimal / 80) * 100, 0),
    100
  );
  const monthMinutes = parseTimeToMinutes(monthTotal);
  const monthHoursDecimal = monthMinutes / 60;

  // Effective selected date for week view sidebar: current selection, or today's date in this week, or first day
  const today = moment();
  const isFutureDay = (date) =>
    moment(date).startOf("day").isAfter(today.clone().startOf("day"));
  const clampWeekOffset = (offset) => Math.min(offset, 0);
  const defaultWeekMoment =
    weekDates.find((d) => d.isSame(today, "day")) || weekDates[0] || null;
  const defaultWeekDateStr = defaultWeekMoment
    ? defaultWeekMoment.format("YYYY-MM-DD")
    : null;
  const selectedWeekDateForDisplay =
    selectedWeekDate &&
    weekDates.some((d) => d.format("YYYY-MM-DD") === selectedWeekDate)
      ? selectedWeekDate
      : defaultWeekDateStr;

  // Number of days in the current week with UNSAVED edits.
  // We track edits via `sliderInteraction` (set in slider handlers and handleTimeChange).
  // Saved data can still exist in `logData` after saving; that should NOT keep the button visible.
  const editedDaysInWeek = weekDates.filter((d) => {
    const key = d.format("YYYY-MM-DD");
    return !!sliderInteraction[key];
  }).length;

  // Check if the current week's month has been finalized
  const isMonthFinalized = submittedWeeks.some((week) => {
    const submittedMonth = moment(week).format("YYYY-MM");
    const currentMonth = moment(currentWeekStart).format("YYYY-MM");
    return submittedMonth === currentMonth;
  });

  // Check if the displayed month's submission is finalized
  const displayedMonth = moment().add(weekOffset, "weeks").format("YYYY-MM");
  const isDisplayedMonthFinalized = submittedWeeks.some((week) => {
    return moment(week).format("YYYY-MM") === displayedMonth;
  });

  // Check if any week in the displayed month is unlocked
  const isDisplayedMonthUnlocked = unlockedWeeks.some((week) => {
    return moment(week).format("YYYY-MM") === displayedMonth;
  });

  // Check if current week is in a submitted month
  const isCurrentWeekInSubmittedMonth = submittedWeeks.some((week) => {
    const weekMonth = moment(week).format("YYYY-MM");
    const currentWeekMonth = moment(currentWeekStart).format("YYYY-MM");
    return weekMonth === currentWeekMonth;
  });

  // Check if current week crosses into another month
  const isCurrentWeekCrossingMonths = () => {
    const weekStart = moment(currentWeekStart);
    const weekEnd = moment(currentWeekStart).add(6, "days");
    return (
      weekStart.month() !== weekEnd.month() ||
      weekStart.year() !== weekEnd.year()
    );
  };

  const fetchSubmittedWeeks = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/submitted-weeks`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        // Handle both old format (array) and new format (object)
        if (Array.isArray(data)) {
          // Old format - just submitted weeks
          setSubmittedWeeks(data);
          setUnlockedWeeks([]);
        } else {
          // New format - both submitted and unlocked weeks
          setSubmittedWeeks(data.submitted || []);
          setUnlockedWeeks(data.unlocked || []);
        }
      } else {
        console.error("Failed to fetch submitted weeks:", res.status);
      }
    } catch (error) {
      console.error("Error fetching submitted weeks:", error);
    }
  }, []);

  const fetchSavedLogs = useCallback(async (dates) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const allLogsData = await res.json();
        const logsArray = Array.isArray(allLogsData) ? allLogsData : [];
        setAllLogs(logsArray); // Save all logs for month calculation
        // Filter to logs from this week
        const thisWeek = {};
        const dateStrings = dates.map((d) => d.format("YYYY-MM-DD"));

        // Initialize with default times for all days
        dateStrings.forEach((dateStr) => {
          thisWeek[dateStr] = initializeDayEntry(dateStr);
        });

        // Override with existing data and load entries
        const entriesByDate = {};
        logsArray.forEach((log) => {
          if (dateStrings.includes(log.date)) {
            thisWeek[log.date] = {
              amStart: log.amStart || "",
              amEnd: log.amEnd || "",
              pmStart: log.pmStart || "",
              pmEnd: log.pmEnd || "",
              rotation: log.rotation || "",
              breakTime: log.breakTime ? (parseFloat(log.breakTime) * 60).toString() : "", // Convert hours to minutes
            };

            // Load entries for this day
            if (log.entries && log.entries.length > 0) {
              entriesByDate[log.date] = log.entries.map((entry) => ({
                id: entry.id,
                activity: entry.rotation || "",
                startTime: entry.startTime || "",
                endTime: entry.endTime || "",
                breakTime: entry.breakTime ? (parseFloat(entry.breakTime) * 60).toString() : "0", // Store in minutes internally
                breakTimeHours: entry.breakTime || "0", // Keep original hours value for display
                comments: entry.comments || "",
                isSaved: true, // Mark as saved
              }));
            }
          }
        });
        setLogData(thisWeek);
        setDayEntries((prev) => ({ ...prev, ...entriesByDate }));
        const commentsByDate = {};
        Object.entries(entriesByDate).forEach(([date, entries]) => {
          const commented = entries.find((entry) => entry.comments);
          if (commented) commentsByDate[date] = commented.comments;
        });
        setSidebarComments(commentsByDate);
        // Reset unsaved edit tracking on load/refresh of saved logs
        setSliderInteraction({});
        // Number of days in this week that actually have saved logs
        const savedDays = dateStrings.filter((dateStr) =>
          logsArray.some((log) => log.date === dateStr)
        ).length;
        setSavedDaysInWeek(savedDays);
        calculateTotal(thisWeek);
        calculateMonthTotal(logsArray);
      } else {
        console.error("Failed to fetch work logs:", res.status);
      }
    } catch (error) {
      console.error("Error fetching work logs:", error);
    }
  }, []);

  const fetchAllVacationRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/vacation-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setAllVacationRequests(data.requests);
      }
    } catch (err) {
      console.error("Error fetching vacation requests:", err);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([fetchSubmittedWeeks(), fetchAllVacationRequests()]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [fetchSubmittedWeeks]);

  // Save view mode & useSlider preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("workHourLogViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("workHourLogUseSlider", JSON.stringify(useSlider));
  }, [useSlider]);

  useEffect(() => {
    localStorage.setItem("workHourLogUseWeekGrid", JSON.stringify(useWeekGrid));
  }, [useWeekGrid]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    let cancelled = false;
    fetch(`${API_ENDPOINTS.CUSTOM_ROTATION_TYPES}/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.rotationTypes) return;
        const names = [...data.rotationTypes]
          .filter(Boolean)
          .sort((a, b) => String(a).localeCompare(String(b)));
        setRotationOptions(names);
      })
      .catch((err) => console.error("Failed to load rotations:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const startOfWeek = moment().add(weekOffset, "weeks").startOf("week"); // Sunday
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(startOfWeek.clone().add(i, "days"));
    }
    setWeekDates(dates);

    fetchSavedLogs(dates);
  }, [weekOffset, fetchSavedLogs]);

  const handleTimeChange = (day, period, value) => {
    if (isFutureDay(day)) return;

    // Get current entry for this day
    const currentEntry = logData[day] || {};

    // For rotation and breakTime, use the value directly (no conversion)
    if (period === "rotation" || period === "breakTime") {
      // Track that the user edited this day (used to show Save Weekly Hrs)
      setSliderInteraction((prev) => ({ ...prev, [day]: true }));

      const updatedEntry = {
        ...currentEntry,
        [period]: value,
      };
      const updated = {
        ...logData,
        [day]: updatedEntry,
      };
      setLogData(updated);
      if (period === "breakTime") {
        calculateTotal(updated);
      }
      return;
    }

    // For time fields, convert blank inputs to "00:00"
    const processedValue = value || "00:00";

    // Track that the user edited this day (used to show Save Weekly Hrs)
    setSliderInteraction((prev) => ({ ...prev, [day]: true }));

    // Create updated entry
    const updatedEntry = {
      ...currentEntry,
      [period]: processedValue,
    };

    if (useWeekGrid && (period === "amStart" || period === "amEnd")) {
      updatedEntry.pmStart = "";
      updatedEntry.pmEnd = "";
    }

    // Validate time ranges to prevent negative hours
    if (period === "amEnd" && updatedEntry.amStart) {
      const startTime = moment(updatedEntry.amStart, "HH:mm");
      const endTime = moment(processedValue, "HH:mm");
      if (endTime.isBefore(startTime)) {
        // If end time is before start time, set end time to start time
        updatedEntry[period] = updatedEntry.amStart;
      }
    } else if (period === "pmEnd" && updatedEntry.pmStart) {
      const startTime = moment(updatedEntry.pmStart, "HH:mm");
      const endTime = moment(processedValue, "HH:mm");
      if (endTime.isBefore(startTime)) {
        // If end time is before start time, set end time to start time
        updatedEntry[period] = updatedEntry.pmStart;
      }
    }

    const updated = {
      ...logData,
      [day]: updatedEntry,
    };
    setLogData(updated);
    calculateTotal(updated);
  };

  const calculateTotal = (data) => {
    let totalMinutes = 0;

    const spanMinutes = (start, end) => {
      if (!start || !end) return 0;
      const read = (time) => {
        if (time === "24:00") return 24 * 60;
        const parsed = moment(time, "HH:mm");
        if (!parsed.isValid()) return null;
        return parsed.hours() * 60 + parsed.minutes();
      };
      const startMinutes = read(start);
      const endMinutes = read(end);
      if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
        return 0;
      }
      return endMinutes - startMinutes;
    };

    Object.values(data).forEach((entry) => {
      let dayMinutes = 0;
      dayMinutes += spanMinutes(entry.amStart, entry.amEnd);
      dayMinutes += spanMinutes(entry.pmStart, entry.pmEnd);
      // Subtract break time (stored in minutes)
      if (entry.breakTime) {
        const breakMins = parseFloat(entry.breakTime) || 0;
        dayMinutes = Math.max(0, dayMinutes - breakMins);
      }
      totalMinutes += dayMinutes;
    });

    const hrs = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const mins = (totalMinutes % 60).toString().padStart(2, "0");
    setWeekTotal(`${hrs}:${mins}`);
  };

  const clockFromMinutes = (minutes) => {
    if (minutes == null) return "";
    if (minutes >= 24 * 60) return "24:00";
    return convertMinutesToTime(minutes);
  };

  const commitWeekGridSelection = ({
    dateStrings,
    startMinutes,
    endMinutes,
    clear = false,
  }) => {
    const updated = { ...logData };
    const changed = [];
    dateStrings.forEach((date) => {
      if (isDayLoggingDisabled(date)) return;
      const current = updated[date] || initializeDayEntry(date);
      let nextEntry;
      if (clear) {
        const kept = entryRanges(current).filter(
          ([rangeStart, rangeEnd]) =>
            !(startMinutes < rangeEnd && endMinutes > rangeStart)
        );
        const am = kept[0];
        const pm = kept[1];
        nextEntry = {
          ...current,
          amStart: clockFromMinutes(am ? am[0] : null),
          amEnd: clockFromMinutes(am ? am[1] : null),
          pmStart: clockFromMinutes(pm ? pm[0] : null),
          pmEnd: clockFromMinutes(pm ? pm[1] : null),
        };
      } else {
        const merged = applyMergedSelection(current, startMinutes, endMinutes);
        nextEntry = {
          ...current,
          amStart: clockFromMinutes(merged.amStart),
          amEnd: clockFromMinutes(merged.amEnd),
          pmStart: clockFromMinutes(merged.pmStart),
          pmEnd: clockFromMinutes(merged.pmEnd),
        };
      }
      const unchanged =
        nextEntry.amStart === (current.amStart || "") &&
        nextEntry.amEnd === (current.amEnd || "") &&
        nextEntry.pmStart === (current.pmStart || "") &&
        nextEntry.pmEnd === (current.pmEnd || "");
      if (unchanged) return;
      updated[date] = nextEntry;
      changed.push(date);
    });
    if (changed.length === 0) return;
    setLogData(updated);
    calculateTotal(updated);
    setSliderInteraction((prev) => {
      const next = { ...prev };
      changed.forEach((date) => {
        next[date] = true;
      });
      return next;
    });
  };

  const handleWeekCategoryChange = (value) => {
    const updated = { ...logData };
    const changed = [];
    weekDates.forEach((day) => {
      const date = day.format("YYYY-MM-DD");
      if (isDayLoggingDisabled(date)) return;
      const current = updated[date] || initializeDayEntry(date);
      if ((current.rotation || "") === value) return;
      updated[date] = { ...current, rotation: value };
      changed.push(date);
    });
    if (changed.length === 0) return;
    setLogData(updated);
    setSliderInteraction((prev) => {
      const next = { ...prev };
      changed.forEach((date) => {
        next[date] = true;
      });
      return next;
    });
  };

  const handleSidebarCommentChange = (date, value) => {
    if (isDayLoggingDisabled(date)) return;
    setSidebarComments((prev) => ({ ...prev, [date]: value }));
    setSliderInteraction((prev) => ({ ...prev, [date]: true }));
  };

  const calculateMonthTotal = (logs) => {
    // Get the current weekStart to determine the month
    const weekStart = moment().add(weekOffset, "weeks").startOf("week");
    const month = weekStart.month();
    const year = weekStart.year();
    let totalMinutes = 0;
    logs.forEach((log) => {
      const logDate = moment(log.date);
      if (logDate.month() === month && logDate.year() === year) {
        totalMinutes += log.totalMinutes || 0;
      }
    });
    const hrs = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const mins = (totalMinutes % 60).toString().padStart(2, "0");
    setMonthTotal(`${hrs}:${mins}`);
  };

  const formatDateLabel = (day) => `${day.format("dddd")} ${day.format("DD")}`;

  // Helper functions for day entries
  const addDayEntry = (date) => {
    const newEntry = {
      id: Date.now(),
      activity: "",
      startTime: "",
      endTime: "",
      breakTime: "",
      comments: "",
    };
    setDayEntries((prev) => ({
      ...prev,
      [date]: [...(prev[date] || []), newEntry],
    }));
  };

  const removeDayEntry = (date, entryId) => {
    setDayEntries((prev) => ({
      ...prev,
      [date]: (prev[date] || []).filter((entry) => entry.id !== entryId),
    }));
  };

  const updateDayEntry = (date, entryId, field, value) => {
    setDayEntries((prev) => ({
      ...prev,
      [date]: (prev[date] || []).map((entry) =>
        entry.id === entryId ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  // Re-order an entry within the same day
  const moveDayEntry = (date, entryId, direction) => {
    setDayEntries((prev) => {
      const entries = prev[date] || [];
      const currentIndex = entries.findIndex((entry) => entry.id === entryId);

      if (currentIndex === -1) {
        return prev;
      }

      const targetIndex =
        direction === "next" ? currentIndex + 1 : currentIndex - 1;

      if (targetIndex < 0 || targetIndex >= entries.length) {
        return prev;
      }

      const updatedEntries = [...entries];
      const [movedEntry] = updatedEntries.splice(currentIndex, 1);
      updatedEntries.splice(targetIndex, 0, movedEntry);

      return {
        ...prev,
        [date]: updatedEntries,
      };
    });
  };

  const calculateDayTotal = (date) => {
    const entries = dayEntries[date] || [];
    let totalMinutes = 0;

    entries.forEach((entry) => {
      if (entry.startTime && entry.endTime) {
        const start = moment(entry.startTime, "HH:mm");
        const end = moment(entry.endTime, "HH:mm");
        if (end.isAfter(start)) {
          const duration = end.diff(start, "minutes");
          const breakMins = entry.breakTime
            ? parseInt(entry.breakTime) || 0
            : 0;
          totalMinutes += Math.max(0, duration - breakMins);
        }
      }
    });

    const hrs = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const mins = (totalMinutes % 60).toString().padStart(2, "0");
    return `${hrs}:${mins}`;
  };

  // Helper to convert time to hours for display (e.g., "10:30" -> "10.50")
  const formatHoursDecimal = (timeString) => {
    if (!timeString) return "0.00";
    const [hours, minutes] = timeString.split(":").map(Number);
    return (hours + minutes / 60).toFixed(2);
  };

  // Slider handlers for day entries
  const handleDayEntrySliderMouseDown = (date, entryId, type) => (e) => {
    e.preventDefault();
    const slider = e.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const startX = e.clientX;
    const entries = dayEntries[date] || [];
    const entry = entries.find((e) => e.id === entryId) || {};
    const defaultStart = "06:00";
    const defaultEnd = "18:00";

    const currentPosition =
      type === "start"
        ? (convertTimeToMinutes(entry.startTime || defaultStart) / 1440) * 100
        : (convertTimeToMinutes(entry.endTime || defaultEnd) / 1440) * 100;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newPosition = Math.max(
        0,
        Math.min(100, currentPosition + (deltaX / rect.width) * 100)
      );
      const minutes = Math.round(((newPosition / 100) * 1440) / 15) * 15;
      const time = convertMinutesToTime(minutes);

      if (type === "start") {
        updateDayEntry(date, entryId, "startTime", time);
      } else {
        const startMins = convertTimeToMinutes(entry.startTime || defaultStart);
        const adjustedMinutes = Math.max(minutes, startMins);
        const adjustedTime = convertMinutesToTime(adjustedMinutes);
        updateDayEntry(date, entryId, "endTime", adjustedTime);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleDayEntrySliderTouchStart = (date, entryId, type) => (e) => {
    const slider = e.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const startX = e.touches[0].clientX;
    const entries = dayEntries[date] || [];
    const entry = entries.find((e) => e.id === entryId) || {};
    const defaultStart = "06:00";
    const defaultEnd = "18:00";

    const currentPosition =
      type === "start"
        ? (convertTimeToMinutes(entry.startTime || defaultStart) / 1440) * 100
        : (convertTimeToMinutes(entry.endTime || defaultEnd) / 1440) * 100;

    const handleTouchMove = (e) => {
      const deltaX = e.touches[0].clientX - startX;
      const newPosition = Math.max(
        0,
        Math.min(100, currentPosition + (deltaX / rect.width) * 100)
      );
      const minutes = Math.round(((newPosition / 100) * 1440) / 15) * 15;
      const time = convertMinutesToTime(minutes);

      if (type === "start") {
        updateDayEntry(date, entryId, "startTime", time);
      } else {
        const startMins = convertTimeToMinutes(entry.startTime || defaultStart);
        const adjustedMinutes = Math.max(minutes, startMins);
        const adjustedTime = convertMinutesToTime(adjustedMinutes);
        updateDayEntry(date, entryId, "endTime", adjustedTime);
      }
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  // Initialize day entries from logData when switching to day view
  useEffect(() => {
    if (viewMode === "day" && selectedDay) {
      const dateStr = selectedDay.format("YYYY-MM-DD");
      const entry = logData[dateStr] || {};
      
      // If no entries exist for this day, initialize from logData
      if (!dayEntries[dateStr] || dayEntries[dateStr].length === 0) {
        const newEntries = [];
        if (entry.amStart && entry.amEnd) {
          newEntries.push({
            id: Date.now(),
            activity: "",
            startTime: entry.amStart,
            endTime: entry.amEnd,
            breakTime: "",
            comments: "",
          });
        }
        if (entry.pmStart && entry.pmEnd) {
          newEntries.push({
            id: Date.now() + 1,
            activity: "",
            startTime: entry.pmStart,
            endTime: entry.pmEnd,
            breakTime: "",
            comments: "",
          });
        }
        if (newEntries.length > 0) {
          setDayEntries((prev) => ({
            ...prev,
            [dateStr]: newEntries,
          }));
        }
      }
    }
  }, [viewMode, selectedDay, logData]);

  // Add auto-submission check
  useEffect(() => {
    const checkAutoSubmit = () => {
      const now = moment();
      const lastDayOfMonth = moment().endOf("month");
      const isLastDayOfMonth = now.isSame(lastDayOfMonth, "day");

      if (
        isLastDayOfMonth &&
        !isMonthFinalized &&
        !isSubmittedWeek &&
        !isUnlockedWeek
      ) {
        // Auto-submit the current week
        const token = localStorage.getItem("token");
        fetch(`${API_ENDPOINTS.RESIDENT}/submit-week`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ weekStart: currentWeekStart }),
        })
          .then((res) => {
            if (res.ok) {
              setSubmittedWeeks([...submittedWeeks, currentWeekStart]);
              alert("Week automatically submitted at the end of the month.");
            }
          })
          .catch((err) => console.error("Auto-submission failed:", err));
      }
    };

    // Check every hour
    const interval = setInterval(checkAutoSubmit, 3600000);
    return () => clearInterval(interval);
  }, [
    currentWeekStart,
    isMonthFinalized,
    isSubmittedWeek,
    isUnlockedWeek,
    submittedWeeks,
    unlockedWeeks,
  ]);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        alert("Please log in again");
        return;
      }

      // Get all logs to save, including leave days
      const logsToSave = weekDates
        .map((day) => {
          const date = day.format("YYYY-MM-DD");
          const blockedType = isBlockedDay(date);

          if (blockedType) {
            // For leave days, create a special log entry
            return {
              date,
              amStart: "00:00",
              amEnd: "00:00",
              pmStart: "00:00",
              pmEnd: "00:00",
              totalMinutes: 0,
              ResidentId: parseInt(userId),
              leaveType: blockedType, // Add leave type to identify vacation/sick leave
            };
          } else {
            // For regular work days, only save if user has interacted or has actual data
            const entry = logData[date] || {};
            const hasInteraction = sliderInteraction[date];
            const hasActualData = hasTimeData(entry);

            // Only save if user has interacted with the slider or has actual time data
            if (hasInteraction || hasActualData || entry.rotation || entry.breakTime) {
              return {
                date,
                amStart: entry.amStart || "",
                amEnd: entry.amEnd || "",
                pmStart: entry.pmStart || "",
                pmEnd: entry.pmEnd || "",
                rotation: entry.rotation || "",
                breakTime: entry.breakTime ? (parseFloat(entry.breakTime) / 60).toFixed(2) : "0", // Convert minutes to hours
                totalMinutes: calculateTotalMinutes(entry),
                ResidentId: parseInt(userId),
              };
            } else {
              // Skip days with no interaction and no data
              return null;
            }
          }
        })
        .filter((log) => log !== null && !isFutureDay(log.date)); // Remove null and future-day entries

      const response = await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ logs: logsToSave }),
      });

      if (response.ok) {
        if (useWeekGrid) {
          const commentDates = Object.keys(sidebarComments);
          for (const date of commentDates) {
            if (isFutureDay(date) || isBlockedDay(date)) continue;
            const entry = logData[date] || {};
            const comments = sidebarComments[date] || "";
            if (!comments && !hasTimeData(entry)) continue;
            const startTime = entry.amStart || entry.pmStart || "";
            const endTime = entry.amEnd || entry.pmEnd || "";
            try {
              await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs/day-entries`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  date,
                  entries: [
                    {
                      rotation: entry.rotation || "",
                      startTime,
                      endTime,
                      breakTime: entry.breakTime
                        ? (parseFloat(entry.breakTime) / 60).toFixed(2)
                        : "0",
                      comments,
                    },
                  ],
                }),
              });
            } catch (commentError) {
              console.error("Failed to save day comment:", commentError);
            }
          }
        }
        const blockedDays = weekDates.filter((day) =>
          isBlockedDay(day.format("YYYY-MM-DD"))
        );
        if (blockedDays.length > 0) {
          setSuccessMessage(
            "Hours saved successfully! (Note: Leave days have been recorded)"
          );
        } else {
          setSuccessMessage("Hours saved successfully!");
        }
        setShowSuccessModal(true);
        // Refresh the data instead of reloading the page
        await fetchSavedLogs(weekDates);
        // After a successful save + refresh, clear weekly edit flags so the Save button hides
        setSliderInteraction({});
      } else {
        const errorData = await response.json();
        alert(`Failed to save hours: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error saving hours:", error);
      alert("Failed to save hours. Please try again.");
    }
  };

  const handleSaveWeekGrid = async () => {
    setSavingWeek(true);
    try {
      await handleSave();
    } finally {
      setSavingWeek(false);
    }
  };

  const handleRequestUnlock = async () => {
    try {
      setRequestError(""); // Clear previous errors
      const token = localStorage.getItem("token");
      if (!token) {
        setRequestError("Authentication token not found.");
        return;
      }
      // Send the first day of the displayed month
      const firstDayOfMonth = moment(weekDates[0])
        .startOf("month")
        .format("YYYY-MM-DD");
      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/request-unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weekStart: firstDayOfMonth }),
      });
      if (res.ok) {
        const data = await res.json();
        setSuccessMessage(data.message || "Unlock request sent successfully!");
        setShowSuccessModal(true);
      } else {
        const errorData = await res.json();
        setRequestError(errorData.message || "Failed to send unlock request.");
      }
    } catch (error) {
      console.error("Error requesting unlock:", error);
      setRequestError("An error occurred while sending the request.");
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        alert("Please log in again");
        return;
      }

      // Get current month and year for submission
      const currentDate = moment().add(weekOffset, "weeks");
      const month = currentDate.month() + 1; // moment months are 0-indexed
      const year = currentDate.year();

      const response = await fetch(`${API_ENDPOINTS.RESIDENT}/submit-month`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          month,
          year,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(
          `Month submitted successfully! ${result.weeksSubmitted} weeks, ${result.workLogsSubmitted} work logs submitted.`
        );
        setShowSuccessModal(true);

        // Update the submitted weeks state to reflect all weeks in the month
        const monthStart = moment(
          `${year}-${month.toString().padStart(2, "0")}-01`
        );
        const monthEnd = moment(monthStart).endOf("month");
        const weeksInMonth = [];
        let currentWeekStart = monthStart.clone().startOf("week");

        while (currentWeekStart.isSameOrBefore(monthEnd)) {
          weeksInMonth.push(currentWeekStart.format("YYYY-MM-DD"));
          currentWeekStart.add(1, "week");
        }

        // Add all weeks in the month to submitted weeks
        setSubmittedWeeks((prev) => [...new Set([...prev, ...weeksInMonth])]);
        setShowConfirmationModal(false);
      } else {
        const errorData = await response.json();
        alert(
          `Failed to submit month: ${errorData.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error submitting month:", error);
      alert("Failed to submit month. Please try again.");
    }
  };

  // Check if current week is the last week of the month
  const isLastWeekOfMonth = () => {
    const weekStart = moment().add(weekOffset, "weeks").startOf("week");
    const weekEnd = weekStart.clone().add(6, "days"); // Saturday
    const lastDayOfMonth = weekEnd.clone().endOf("month");
    const daysUntilEndOfMonth = lastDayOfMonth.diff(weekEnd, "days");
    const result = daysUntilEndOfMonth <= 6; // If 6 or fewer days until end of month

    return result;
  };

  // Add slider styles
  const sliderContainerStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    minWidth: "300px",
    gap: "0.5rem",
    padding: "0.5rem",
  };

  const sliderTrackStyle = {
    position: "relative",
    width: "100%",
    height: "8px",
    backgroundColor: "var(--gray-200)",
    borderRadius: "4px",
    margin: "0.5rem 0",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
    zIndex: 2,
  };

  const sliderThumbStyle = (
    position,
    hasUserInteraction = false,
    hasTimeData = false
  ) => ({
    position: "absolute",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background:
      hasUserInteraction || hasTimeData
        ? "var(--gray-100)"
        : "var(--light-gradient)",
    cursor: "pointer",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: `${position}%`,
    zIndex: 4,
    boxShadow:
      hasUserInteraction || hasTimeData
        ? "0 2px 8px rgba(0,0,0,0.2)"
        : "0 1px 3px rgba(0,0,0,0.1)",
    border:
      hasUserInteraction || hasTimeData
        ? "2px solid var(--blue)"
        : "2px solid var(--gray-400)",
  });

  const progressBarStyle = (
    start,
    end,
    hasUserInteraction = false,
    hasTimeData = false
  ) => ({
    position: "absolute",
    height: "8px",
    background:
      hasUserInteraction || hasTimeData
        ? "var(--main-gradient)"
        : "var(--gray-400)",
    top: "50%",
    transform: "translateY(-50%)",
    left: `${start}%`,
    width: `${end - start}%`,
    borderRadius: "4px",
    zIndex: 3,
    boxShadow:
      hasUserInteraction || hasTimeData ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
  });

  const tickMarksStyle = {
    position: "absolute",
    width: "100%",
    height: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    justifyContent: "space-between",
    pointerEvents: "none",
    zIndex: 1,
  };

  const tickMarkStyle = {
    width: "1px",
    height: "12px",
    backgroundColor: "var(--gray-200)",
    borderRadius: "1px",
    zIndex: 0,
  };

  const timeDisplayStyle = {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: "14px",
    color: "var(--text-color)",
    highlight: "none",
    fontWeight: "500",
    WebkitUserSelect: "none",
    MsUserSelect: "none",
    UserSelect: "none",
  };

  const convertTimeToMinutes = (time) => {
    if (!time) return 0;
    if (time === "24:00") return 24 * 60;
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const convertMinutesToTime = (minutes) => {
    if (minutes >= 24 * 60) return "24:00";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const calculateTotalMinutes = (entry) => {
    // If no time data exists, return 0
    if (!hasTimeData(entry)) {
      return 0;
    }

    const amStart = convertTimeToMinutes(entry.amStart || "");
    const amEnd = convertTimeToMinutes(entry.amEnd || "");
    const pmStart = convertTimeToMinutes(entry.pmStart || "");
    const pmEnd = convertTimeToMinutes(entry.pmEnd || "");

    const amMinutes = amEnd - amStart;
    const pmMinutes = pmEnd - pmStart;
    const breakMins = entry.breakTime ? parseFloat(entry.breakTime) || 0 : 0;

    return Math.max(0, Math.max(0, amMinutes) + Math.max(0, pmMinutes) - breakMins);
  };

  const handleSliderChange = (date, field, value) => {
    const time = convertMinutesToTime(value);

    // Get current entry for this date
    const currentEntry = logData[date] || initializeDayEntry(date);
    const defaultTimes = getDefaultTimePresets();

    // Validate to prevent negative hours
    if (field === "amEnd") {
      const startTime = convertTimeToMinutes(
        currentEntry.amStart || defaultTimes.amStart
      );
      const endTime = value;
      if (endTime < startTime) {
        // If end time would be before start time, set it to start time
        handleTimeChange(date, field, convertMinutesToTime(startTime));
        return;
      }
    }

    handleTimeChange(date, field, time);
  };

  // Generate tick marks for every hour
  const generateTickMarks = () => {
    const ticks = [];
    for (let i = 0; i <= 24; i++) {
      ticks.push(<div key={i} style={tickMarkStyle} />);
    }
    return ticks;
  };

  const handleSliderMouseDown = (date, type) => (e) => {
    if (isFutureDay(date)) return;

    const slider = e.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const startX = e.clientX;
    const currentEntry = logData[date] || initializeDayEntry(date);
    const defaultTimes = getDefaultTimePresets();
    const currentPosition =
      type === "start"
        ? (convertTimeToMinutes(currentEntry.amStart || defaultTimes.amStart) /
            1440) *
          100
        : (convertTimeToMinutes(currentEntry.amEnd || defaultTimes.amEnd) /
            1440) *
          100;

    // Mark this date as having user interaction
    setSliderInteraction((prev) => ({ ...prev, [date]: true }));

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const newPosition = Math.max(
        0,
        Math.min(100, currentPosition + (deltaX / rect.width) * 100)
      );
      const minutes = Math.round(((newPosition / 100) * 1440) / 15) * 15;

      if (type === "start") {
        handleSliderChange(date, "amStart", minutes);
      } else {
        // For end time, ensure it's not before start time
        const startMinutes = convertTimeToMinutes(
          currentEntry.amStart || defaultTimes.amStart
        );
        const adjustedMinutes = Math.max(minutes, startMinutes);
        handleSliderChange(date, "amEnd", adjustedMinutes);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Add touch support for slider
  const handleSliderTouchStart = (date, type) => (e) => {
    if (isFutureDay(date)) return;

    const slider = e.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const startX = e.touches[0].clientX;
    const currentEntry = logData[date] || initializeDayEntry(date);
    const defaultTimes = getDefaultTimePresets();
    const currentPosition =
      type === "start"
        ? (convertTimeToMinutes(currentEntry.amStart || defaultTimes.amStart) /
            1440) *
          100
        : (convertTimeToMinutes(currentEntry.amEnd || defaultTimes.amEnd) /
            1440) *
          100;

    // Mark this date as having user interaction
    setSliderInteraction((prev) => ({ ...prev, [date]: true }));

    const handleTouchMove = (e) => {
      const deltaX = e.touches[0].clientX - startX;
      const newPosition = Math.max(
        0,
        Math.min(100, currentPosition + (deltaX / rect.width) * 100)
      );
      const minutes = Math.round(((newPosition / 100) * 1440) / 15) * 15;

      if (type === "start") {
        handleSliderChange(date, "amStart", minutes);
      } else {
        // For end time, ensure it's not before start time
        const startMinutes = convertTimeToMinutes(
          currentEntry.amStart || defaultTimes.amStart
        );
        const adjustedMinutes = Math.max(minutes, startMinutes);
        handleSliderChange(date, "amEnd", adjustedMinutes);
      }
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  // Add function to get current academic year
  const getCurrentAcademicYear = () => {
    const now = new Date();
    const year =
      now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
  };

  // Function to get default time presets (6:00 AM to 6:00 PM)
  const getDefaultTimePresets = () => {
    return {
      amStart: "06:00",
      amEnd: "18:00",
    };
  };

  // Function to initialize a new day entry with empty times
  const initializeDayEntry = (dayString) => {
    return {
      amStart: "",
      amEnd: "",
      pmStart: "",
      pmEnd: "",
      rotation: "",
      breakTime: "",
    };
  };

  // Function to check if an entry has actual time data
  const hasTimeData = (entry) => {
    return (
      entry && (entry.amStart || entry.amEnd || entry.pmStart || entry.pmEnd)
    );
  };

  // Define handleAcademicYearChange as a regular function
  const handleAcademicYearChange = async (year) => {
    setSelectedAcademicYear(year);
    try {
      const token = localStorage.getItem("token");
      const [startYear] = year.split("-");
      const res = await fetch(
        `${API_ENDPOINTS.RESIDENT}/leave-allotment/${startYear}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setLeaveAllotment(data);
      }

      // Fetch and filter vacation requests for the selected academic year
      const requestsRes = await fetch(
        `${API_ENDPOINTS.RESIDENT}/vacation-requests`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        const startDate = moment(`${startYear}-07-01`);
        const endDate = moment(`${parseInt(startYear) + 1}-06-30`);

        // Filter requests that fall within the selected academic year
        const filteredRequests = requestsData.requests.filter((request) => {
          const requestStart = moment(request.startDate);
          const requestEnd = moment(request.endDate);
          return (
            requestStart.isSameOrAfter(startDate) &&
            requestEnd.isSameOrBefore(endDate)
          );
        });

        setVacationRequests(filteredRequests);
        setVacationRequestError("");
      }
    } catch (err) {
      console.error("Error fetching leave allotment or requests:", err);
    }
  };

  // Update useEffect for vacation tab
  useEffect(() => {
    if (activeTab === "vacation") {
      const currentYear = getCurrentAcademicYear();
      handleAcademicYearChange(currentYear);
    }
  }, [activeTab]);

  // Remove the old fetchVacationRequests function since its functionality is now in handleAcademicYearChange
  const fetchVacationRequests = () => {
    const currentYear = getCurrentAcademicYear();
    handleAcademicYearChange(currentYear);
  };

  // Update the isBlockedDay function to use allVacationRequests instead of vacationRequests
  const isBlockedDay = (date) => {
    const found = allVacationRequests.find(
      (r) =>
        r.status === "Approved" &&
        moment(date).isBetween(
          moment(r.startDate),
          moment(r.endDate),
          "day",
          "[]" // Include the start and end dates
        )
    );
    return found ? found.type : null;
  };

  const isDayLoggingDisabled = (dateStr) =>
    isFutureDay(dateStr) ||
    isBlockedDay(dateStr) !== null ||
    (isCurrentWeekInSubmittedMonth && !isDisplayedMonthUnlocked) ||
    (isSubmittedWeek && !isUnlockedWeek && !isDisplayedMonthUnlocked);

  // Add a helper to check for overlapping requests and past dates
  const isDateInPast = (date) => moment(date).isBefore(moment().startOf("day"));
  const isOverlappingRequest = (start, end) => {
    const startDate = moment(start);
    const endDate = moment(end);
    return vacationRequests.some((r) => {
      const reqStart = moment(r.startDate);
      const reqEnd = moment(r.endDate);
      // Overlap if start <= reqEnd && end >= reqStart
      return (
        startDate.isSameOrBefore(reqEnd) && endDate.isSameOrAfter(reqStart)
      );
    });
  };
  const isInvalidRequest = () => {
    if (!requestStart || !requestEnd) return true;
    if (isDateInPast(requestStart) || isDateInPast(requestEnd)) return true;
    if (moment(requestEnd).isBefore(moment(requestStart))) return true;
    if (isOverlappingRequest(requestStart, requestEnd)) return true;

    // Check if request falls within selected academic year
    const [startYear] = selectedAcademicYear.split("-");
    const yearStart = moment(`${startYear}-07-01`);
    const yearEnd = moment(`${parseInt(startYear) + 1}-06-30`);
    const requestStartDate = moment(requestStart);
    const requestEndDate = moment(requestEnd);

    if (
      !requestStartDate.isSameOrAfter(yearStart) ||
      !requestEndDate.isSameOrBefore(yearEnd)
    ) {
      return true;
    }

    return false;
  };
  const getRequestValidationError = () => {
    if (!requestStart || !requestEnd) return "";
    if (isDateInPast(requestStart) || isDateInPast(requestEnd))
      return "Cannot request vacation/sick leave for past dates.";
    if (moment(requestEnd).isBefore(moment(requestStart)))
      return "End date cannot be before start date.";
    if (isOverlappingRequest(requestStart, requestEnd))
      return "You already have a request for these dates.";

    // Add academic year validation error
    const [startYear] = selectedAcademicYear.split("-");
    const yearStart = moment(`${startYear}-07-01`);
    const yearEnd = moment(`${parseInt(startYear) + 1}-06-30`);
    const requestStartDate = moment(requestStart);
    const requestEndDate = moment(requestEnd);

    if (
      !requestStartDate.isSameOrAfter(yearStart) ||
      !requestEndDate.isSameOrBefore(yearEnd)
    ) {
      return `Request must fall within the selected academic year (${yearStart.format(
        "MMM D, YYYY"
      )} to ${yearEnd.format("MMM D, YYYY")})`;
    }

    return "";
  };

  // Helper function to calculate workdays and weekends
  const calculateWorkdaysAndWeekends = (startDate, endDate) => {
    let workdays = 0;
    let weekends = 0;
    const current = moment(startDate);
    const end = moment(endDate);
    
    while (current.isSameOrBefore(end, 'day')) {
      const dayOfWeek = current.day(); // 0 = Sunday, 6 = Saturday
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        weekends++;
      } else {
        workdays++;
      }
      current.add(1, 'day');
    }
    
    const parts = [];
    if (workdays > 0) {
      parts.push(`${workdays}Days,`);
    }
    if (weekends > 0) {
      parts.push(`${weekends}Wnd`);
    }
    
    return parts.length > 0 ? parts.join(' ') : '0Days';
  };

  // Utility: parse hash string to object
  const parseHash = useCallback(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(hash);
    return {
      tab: params.get("tab") || "work",
      weekOffset: params.get("weekOffset")
        ? parseInt(params.get("weekOffset"))
        : 0,
    };
  }, []);

  // Utility: update hash from state
  const updateHash = useCallback((tab, weekOffset) => {
    const params = new URLSearchParams();
    params.set("tab", tab);
    if (tab === "work") {
      params.set("weekOffset", weekOffset);
    }
    window.location.hash = params.toString();
  }, []);

  // On mount: set state from hash
  useEffect(() => {
    const { tab, weekOffset: hashWeekOffset } = parseHash();
    setActiveTab(tab);
    setWeekOffset(clampWeekOffset(hashWeekOffset));
  }, [parseHash]);

  // Listen for hash changes (back/forward)
  useEffect(() => {
    const onHashChange = () => {
      const { tab, weekOffset: hashWeekOffset } = parseHash();
      setActiveTab(tab);
      setWeekOffset(clampWeekOffset(hashWeekOffset));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [parseHash]);

  // Whenever weekOffset/tab changes, update hash
  useEffect(() => {
    updateHash(activeTab, clampWeekOffset(weekOffset));
  }, [activeTab, weekOffset, updateHash]);

  useEffect(() => {
    if (weekOffset > 0) {
      setWeekOffset(0);
    }
  }, [weekOffset]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Set mobile current day to today when week dates change
  useEffect(() => {
    if (weekDates.length > 0) {
      const today = moment();
      const todayIndex = weekDates.findIndex((date) =>
        date.isSame(today, "day")
      );
      if (todayIndex !== -1) {
        setMobileCurrentDayIndex(todayIndex);
      } else {
        // If today is not in current week, set to first day
        setMobileCurrentDayIndex(0);
      }
    }
  }, [weekDates]);

  // Mobile view functions
  const getNextMobileDay = () => {
    if (weekDates.length === 0) return null;
    if (mobileCurrentDayIndex < weekDates.length - 1) {
      return weekDates[mobileCurrentDayIndex + 1];
    }
    if (weekOffset < 0) {
      return moment().add(weekOffset + 1, "weeks").startOf("week");
    }
    return null;
  };

  const canNavigateMobileNext = (() => {
    const nextDay = getNextMobileDay();
    return nextDay && !isFutureDay(nextDay);
  })();

  const handleMobileDayChange = (direction) => {
    if (direction === "prev") {
      if (mobileCurrentDayIndex > 0) {
        // Move to previous day in current week
        setMobileCurrentDayIndex(mobileCurrentDayIndex - 1);
      } else {
        // Move to previous week
        setWeekOffset(weekOffset - 1);
        setMobileCurrentDayIndex(6); // Start at last day of previous week
      }
    } else if (direction === "next") {
      const nextDay = getNextMobileDay();
      if (!nextDay || isFutureDay(nextDay)) {
        return;
      }

      if (mobileCurrentDayIndex < weekDates.length - 1) {
        // Move to next day in current week
        setMobileCurrentDayIndex(mobileCurrentDayIndex + 1);
      } else if (weekOffset < 0) {
        // Move to next week (only allowed when viewing past weeks)
        setWeekOffset(weekOffset + 1);
        setMobileCurrentDayIndex(0); // Start at first day of next week
      }
    }
  };

  const handleMobileSave = async () => {
    const currentDay = weekDates[mobileCurrentDayIndex];
    const dayString = currentDay.format("YYYY-MM-DD");
    if (isFutureDay(dayString)) {
      alert("You cannot log work hours for future dates.");
      return;
    }

    const entry = logData[dayString] || {};

    setMobileSaving(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please log in to save work hours");
        return;
      }

      const baseLog = {
        date: dayString,
        amStart: entry.amStart || "",
        amEnd: entry.amEnd || "",
        pmStart: entry.pmStart || "",
        pmEnd: entry.pmEnd || "",
        rotation: entry.rotation || "",
        breakTime: entry.breakTime
          ? (parseFloat(entry.breakTime) / 60).toFixed(2)
          : "0",
        totalMinutes: calculateTotalMinutes(entry),
      };

      const response = await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          logs: [baseLog],
        }),
      });

      if (response.ok) {
        const comments = sidebarComments[dayString] || "";

        // Optionally persist a single day-entry with comments for this date
        if (hasTimeData(entry) || entry.breakTime || comments) {
          await fetch(
            `${API_ENDPOINTS.RESIDENT}/work-logs/day-entries`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                date: dayString,
                entries: [
                  {
                    rotation: entry.rotation || "",
                    startTime: entry.amStart || "",
                    endTime: entry.amEnd || "",
                    breakTime: entry.breakTime
                      ? (parseFloat(entry.breakTime) / 60).toFixed(2)
                      : "0",
                    comments,
                  },
                ],
              }),
            }
          );
        }

        setSuccessMessage("Work hours saved successfully!");
        setShowSuccessModal(true);
        // Refresh the data
        fetchSavedLogs(weekDates);
      } else {
        const errorData = await response.json();
        alert(
          `Failed to save work hours: ${errorData.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error saving work hours:", error);
      alert("Failed to save work hours. Please try again.");
    } finally {
      setMobileSaving(false);
    }
  };

  const handleSidebarDaySave = async (date) => {
    if (!date) return;
    if (isFutureDay(date)) {
      alert("You cannot log work hours for future dates.");
      return;
    }

    const entry = logData[date] || {};
    const comments = sidebarComments[date] || "";

    // Only save if there is any actual data
    if (!hasTimeData(entry) && !entry.breakTime && !comments) {
      return;
    }

    setSidebarSaving(true);

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        alert("Please log in again");
        return;
      }

      const baseLog = {
        date,
        amStart: entry.amStart || "",
        amEnd: entry.amEnd || "",
        pmStart: entry.pmStart || "",
        pmEnd: entry.pmEnd || "",
        rotation: entry.rotation || "",
        breakTime: entry.breakTime
          ? (parseFloat(entry.breakTime) / 60).toFixed(2)
          : "0",
        totalMinutes: calculateTotalMinutes(entry),
        ResidentId: parseInt(userId, 10),
      };

      // Save/update the core work‑log record
      await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ logs: [baseLog] }),
      });

      // Also save a single day‑entry with comments for this date
      const dayEntryPayload = {
        rotation: entry.rotation || "",
        startTime: entry.amStart || "",
        endTime: entry.amEnd || "",
        breakTime: entry.breakTime
          ? (parseFloat(entry.breakTime) / 60).toFixed(2)
          : "0",
        comments,
      };

      await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs/day-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          entries: [dayEntryPayload],
        }),
      });

      setSuccessMessage("Day updated successfully!");
      setShowSuccessModal(true);

      // Refresh week data so totals and break times stay in sync
      fetchSavedLogs(weekDates);
    } catch (error) {
      console.error("Error saving day from sidebar:", error);
      alert("Failed to save changes. Please try again.");
    } finally {
      setSidebarSaving(false);
    }
  };

  const handleToggleView = () => {
    if (viewMode === "week") {
      setViewMode("day");
      if (!selectedDay) {
        const today = moment();
        setSelectedDay(today);
        const todayWeekStart = today.clone().startOf("week");
        const currentWeekStart = moment().add(weekOffset, "weeks").startOf("week");
        const weekDiff = todayWeekStart.diff(currentWeekStart, "weeks");
        if (weekDiff !== 0) {
          setWeekOffset((prev) => prev + weekDiff);
        }
      }
    } else {
      setViewMode("week");
      setSelectedDay(null);
    }
  };

  const handleDayNavigation = (direction) => {
    const baseDay = selectedDay
      ? selectedDay.clone()
      : weekDates[0]
      ? weekDates[0].clone()
      : null;

    if (!baseDay) {
      return;
    }

    const delta = direction === "next" ? 1 : -1;
    const newDay = baseDay.add(delta, "days");
    if (direction === "next" && isFutureDay(newDay)) {
      return;
    }

    const currentWeekStart = moment().add(weekOffset, "weeks").startOf("week");
    const newWeekStart = newDay.clone().startOf("week");
    const weekDiff = newWeekStart.diff(currentWeekStart, "weeks");

    if (weekDiff !== 0) {
      setWeekOffset((prev) => prev + weekDiff);
    }

    setSelectedDay(newDay);
  };

  const handleNavigateToToday = () => {
    const today = moment();
    const todayWeekStart = today.clone().startOf("week");
    const baseWeekStart = moment().startOf("week");
    const newOffset = todayWeekStart.diff(baseWeekStart, "weeks");

    setWeekOffset(newOffset);
    setSelectedDay(today);
  };

  // Export vacation/sick leave to calendar
  const handleExportToCalendar = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to export events");
      return;
    }

    setExportLoading(true);
    setExportSuccess(false);

    try {
      // Check if there are any approved requests to export
      const approvedRequests = vacationRequests.filter(
        (r) => r.status === "Approved"
      );
      if (approvedRequests.length === 0) {
        alert("No approved vacation or sick leave requests found to export.");
        setExportLoading(false);
        return;
      }

      // 1. Get existing calendar events
      let calendarEvents = [];
      try {
        const res = await fetch(`${API_ENDPOINTS.EVENTS}/my-events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          calendarEvents = await res.json();
        } else {
          console.error(
            "Failed to fetch existing events:",
            res.status,
            res.statusText
          );
        }
      } catch (err) {
        console.error("Failed to fetch existing events:", err);
      }

      // 2. Filter exported events and separate past from future
      const exportedEvents = calendarEvents.filter(
        (e) =>
          (e.label === "Vacation" || e.label === "Sick") &&
          e.description &&
          e.description.includes("[exported-from-workhourlog]")
      );

      const now = moment();
      const pastExportedEvents = exportedEvents.filter((e) =>
        moment(e.start).isBefore(now.startOf("day"))
      );
      const futureExportedEvents = exportedEvents.filter((e) =>
        moment(e.start).isSameOrAfter(now.startOf("day"))
      );

      // 3. Remove only future exported events (keep past events)
      let deletedCount = 0;
      for (const event of futureExportedEvents) {
        try {
          const deleteRes = await fetch(`${API_ENDPOINTS.EVENTS}/${event.id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (deleteRes.ok) {
            deletedCount++;
          } else {
            console.error(
              `Failed to delete event ${event.id}:`,
              deleteRes.status,
              deleteRes.statusText
            );
          }
        } catch (err) {
          console.error(`Failed to delete event ${event.id}:`, err);
        }
      }

      // 4. Create new events for approved vacation/sick leave requests
      const newEvents = [];

      approvedRequests.forEach((r) => {
        const type = r.type;
        // Ensure dates are parsed correctly and normalized to start of day
        let curr = moment(r.startDate).startOf("day");
        const end = moment(r.endDate).startOf("day");

        console.log(`Processing ${type} request: ${curr.format("YYYY-MM-DD")} to ${end.format("YYYY-MM-DD")}`);

        while (curr.isSameOrBefore(end, "day")) {
          console.log(`Creating event for: ${curr.format("YYYY-MM-DD")}`);
          
          // Only create events for future dates or today
          if (curr.isSameOrAfter(now.startOf("day"), "day")) {
            // Check if this date already has a past exported event
            const existingPastEvent = pastExportedEvents.find(
              (e) =>
                moment(e.start).isSame(curr, "day") && e.label === type
            );

            // Only create if no past event exists for this date
            if (!existingPastEvent) {
              // Create all-day event from 12:00 AM to 11:59 PM
              const eventStart = curr.startOf("day").toDate();
              const eventEnd = curr.endOf("day").toDate();

              newEvents.push({
                title: `${type} Leave`,
                start: eventStart,
                end: eventEnd,
                allDay: true,
                label: type,
                description: `[exported-from-workhourlog] ${type} leave exported from WorkHourLog - All day event from 12:00 AM to 11:59 PM`,
                location: "Leave",
                repeat: "none",
              });
            }
          }
          curr.add(1, "day");
        }
      });

      // 5. Add new events to calendar
      let successCount = 0;
      let failCount = 0;

      for (const event of newEvents) {
        try {
          const response = await fetch(`${API_ENDPOINTS.EVENTS}/add`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(event),
          });

          if (response.ok) {
            successCount++;
            console.log(
              `Successfully created ${event.label} event for ${moment(
                event.start
              ).format("YYYY-MM-DD")}`
            );
          } else {
            const errorData = await response.json();
            console.error(`Failed to create event:`, errorData);
            failCount++;
          }
        } catch (err) {
          console.error("Failed to add event:", err);
          failCount++;
        }
      }

      // 6. Show success message with details
      if (successCount > 0) {
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 5000);

        let message = `Successfully exported ${successCount} leave events to calendar`;
        if (deletedCount > 0) {
          message += ` (removed ${deletedCount} existing events)`;
        }
        if (failCount > 0) {
          message += ` (${failCount} failed)`;
        }

        console.log(message);
      } else if (failCount > 0) {
        alert(`Failed to export any events to calendar. Please try again.`);
      } else {
        alert(
          `No new leave events to export (all events already exist in calendar).`
        );
      }
    } catch (error) {
      console.error("Export to calendar failed:", error);
      alert("Failed to export to calendar. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Resident Timesheets" />
        <div className="bg-white shadow-sm">
          <Container>
            <Row>
              <Col
                lg={2}
                md={6}
                sm={6}
                className=" d-flex col-6 order-first order-lg-1 order-md-1 order-sm-1  align-items-center justify-content-start"
              >
                <h6 className="mb-0 fw-semibold py-2 py-md-2 py-lg-0">Timesheets</h6>
              </Col>
              <Col
                lg={6}
                md={12}
                sm={12}
                className="order-last order-lg-2 order-md-3 order-sm-3"
              >
                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                  <Nav variant="tabs" style={{ marginBottom: "-1px" }}>
                    <Nav.Item>
                      <Nav.Link eventKey="work">Work Hours</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="vacation">Leave Request</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Tab.Container>
              </Col>
              <Col
                lg={4}
                md={6}
                sm={6}
                className="d-flex col-6 order-lg-3 order-md-2 order-sm-2 order-2 py-2 py-md-2 py-lg-0 align-items-center justify-content-end"
              >
                {activeTab === "work" && (
                  <Button
                    variant={useSlider ? "primary" : "outline-primary"}
                    onClick={() => setUseSlider(!useSlider)}
                    size="sm"
                    className="d-flex align-items-center gap-2"
                  >
                    {useSlider ? (
                      <>
                        <div className="d-flex align-items-center gap-2">
                        <Clock/>
                        <span>Time Input</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex align-items-center gap-2">
                        <SlidersHorizontal/>
                        <span>Time Slider</span>
                        </div>
                        <span>Time Slider</span>
                      </>
                    )}
                  </Button>
                )}
                {activeTab === "vacation" && (
                  <Button
                    variant="primary"
                    onClick={() => setShowRequestModal(true)}
                    size="sm"
                  >
                    Request Leave
                  </Button>
                )}
              </Col>
            </Row>
          </Container>
        </div>
        <Container
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <div className="text-center">
            <Spinner
              animation="border"
              variant="primary"
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading timesheets...</div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <PageTitle
        title="Resident Timesheets"
        description="Log your work hours and track compliance"
      />
      <Navbar pageTitle="Resident Timesheets" />

      {/* Desktop View */}
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col
              lg={2}
              md={4}
              sm={4}
              className=" d-flex col-4 order-first order-lg-1 order-md-1 order-sm-1 mt-1 mt-md-2 mt-lg-0 align-items-center justify-content-start"
            >
              <h6 className="mb-0 fw-semibold">Timesheets</h6>
            </Col>
            <Col
              lg={6}
              md={12}
              sm={12}
              className="order-last order-lg-2 order-md-3 order-sm-3"
            >
              <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" style={{ marginBottom: "-1px" }}>
                  <Nav.Item>
                    <Nav.Link eventKey="work">Work Hours</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="vacation">Leave Request</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Tab.Container>
            </Col>
            <Col
              lg={4}
              md={8}
              sm={8}
              className="d-flex col-8 order-lg-3 order-md-2 order-sm-2 mt-1 mt-md-1 mt-lg-0 align-items-center justify-content-end"
            >
              {activeTab === "work" && (
                <div className="d-flex flex-row gap-2 align-items-center">
                  <div className="d-none d-lg-flex align-items-center">
                    <Form.Check
                      type="switch"
                      id="work-hour-week-grid"
                      label="Week grid"
                      className="mb-0"
                      checked={useWeekGrid}
                      onChange={(event) => setUseWeekGrid(event.target.checked)}
                    />
                  </div>
                  {/*<div className="radio-inputs" style={{ width: 'fit-content', minWidth: 80 }}>
                    <label className="radio">
                      <input
                        type="radio"
                        name="viewMode"
                        value="week"
                        checked={viewMode === "week"}
                        onChange={() => handleToggleView("week")}
                        aria-label="Week View"
                      />
                      <span className="name d-flex align-items-center gap-2 px-md-2">
                        <i className="bi bi-calendar-week"></i>
                        <span className="d-none d-md-inline">Week</span>
                      </span>
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        name="viewMode"
                        value="day"
                        checked={viewMode === "day"}
                        onChange={() => handleToggleView("day")}
                        aria-label="Day View"
                      />
                      <span className="name d-flex align-items-center gap-2 px-md-2">
                        <i className="bi bi-calendar-day"></i>
                        <span className="d-none d-md-inline">Day</span>
                      </span>
                    </label>
                  </div>*/}
                  {isMobile && (
                    <div className="radio-inputs" style={{ width: "fit-content", minWidth: 80 }}>
                      <label className="radio mb-0">
                        <input
                          type="radio"
                          name="timeInputMode"
                          checked={!useSlider}
                          onChange={() => setUseSlider(false)}
                          aria-label="Switch to Time Input"
                        />
                        <span className="name d-flex align-items-center gap-2">
                          <i className="bi d-flex align-items-center gap-2"><Clock/></i>
                        </span>
                      </label>
                      <label className="radio mb-0">
                        <input
                          type="radio"
                          name="timeInputMode"
                          checked={useSlider}
                          onChange={() => setUseSlider(true)}
                          aria-label="Switch to Time Slider"
                        />
                        <span className="name d-flex align-items-center gap-2">
                          <SlidersHorizontal/>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "vacation" && (
                <div className="d-flex flex-row gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleExportToCalendar}
                    disabled={exportLoading}
                    className="d-flex flex-row align-items-center justify-content-center"
                  >
                    {exportLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <div className="d-flex align-items-center">
                        <Forward/>
                        <span className="ms-2 d-none d-md-block">Export</span></div>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowRequestModal(true)}
                    size="sm"
                  >
                      Request Leave
                    </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt-4">
        <Tab.Content>
          <Tab.Pane eventKey="work" active={activeTab === "work"}>

              <Row className="g-3 mb-4">
                <Col xs={6} md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-bold">
                          Weekly Total
                        </span>
                        <span className="bg-primary rounded-2 px-2 py-1"><Clock/></span>
                      </div>
                      <div className="d-flex align-items-baseline gap-2 mb-2">
                        <span className="fw-bold fs-3">
                          {weekHoursDecimal.toFixed(1)}
                        </span>
                        <span className="text-muted small">of 80 hours</span>
                      </div>
                      <div className="progress" style={{ height: "6px" }}>
                        <div
                          className={`progress-bar ${weekMinutes > 4800 ? "bg-danger" : ""}`}
                          role="progressbar"
                          style={{
                            width: `${Math.min(
                              Math.max(weeklyPercentOf80, 0),
                              100
                            ).toFixed(0)}%`,
                            ...(weekMinutes > 4800
                              ? {
                                  background:
                                    "linear-gradient(90deg, var(--red) 0%,rgb(218, 24, 121) 100%)",
                                }
                              : {}),
                          }}
                        ></div>
                      </div>
                      <div className="mt-2">
                        <span
                          className={`small bg-success rounded-pill px-3 py-1 fw-semibold ${
                            weekMinutes > 4800 ? "bg-danger" : "bg-success"
                          }`}
                        >
                          {weekMinutes > 4800 ? "Over limit" : "On track"}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-bold">
                          Daily Average
                        </span>
                        <span className="bg-warning rounded-2 px-2 py-1"><ChartColumnStacked/></span>
                      </div>
                      <div className="d-flex align-items-baseline gap-2 mb-1">
                        <span className="fw-bold fs-3">
                          {dailyAverageHours.toFixed(1)}
                        </span>
                        <span className="text-muted small">hours per day</span>
                      </div>
                      <span className="text-muted small">
                        Based on the current week
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-bold">
                          Compliance
                        </span>
                        <span className="bg-info rounded-2 px-2 py-1"><ShieldCheck/></span>
                      </div>
                      <div className="d-flex align-items-baseline gap-2 mb-1">
                        <span className="fw-bold fs-3">
                          {complianceRate.toFixed(0)}%
                        </span>
                      </div>
                      <span
                        className={`small bg-success rounded-pill px-3 py-1 fw-semibold ${
                          weekMinutes > 4800 ? "bg-danger" : "bg-success"
                        }`}
                      >
                        {weekMinutes > 4800 ? "Over 80-hour limit" : "Compliant"}
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-bold">
                          This Month
                        </span>
                        <span className="bg-cyan rounded-2 px-2 py-1"><TrendingUp/></span>
                      </div>
                      <div className="d-flex align-items-baseline gap-2 mb-1">
                        <span className="fw-bold fs-3">
                          {monthHoursDecimal.toFixed(1)}
                        </span>
                        <span className="text-muted small">
                          total hours logged
                        </span>
                      </div>
                      <span className="text-muted small">
                        Updated from submitted logs
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            
            
            {/* Mobile View */}
            {isMobile && (
              <div className="d-md-none">

                  <Card className="border-0 shadow-sm">
                    <Card.Header
                      className="border-0 p-0"
                      style={{ background: "linear-gradient(45deg, var(--blue) 0%, var(--green) 70%, var(--cyan) 100%)" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2 px-3 pt-3 pb-1">
                        <Button
                          variant="light-solid"
                          size="sm"
                          onClick={() => handleMobileDayChange("prev")}
                        >
                          <ChevronLeft/>
                        </Button>

                        <div className="text-center text-white">
                          <h6 className="mb-0 fw-semibold">
                            {weekDates[mobileCurrentDayIndex]?.format(
                              "dddd, MMM DD"
                            )}
                          </h6>
                          <small className="text-white-75">
                            Week {moment().add(weekOffset, "weeks").week()} -{" "}
                            {moment()
                              .add(weekOffset, "weeks")
                              .format("MMMM YYYY")}
                          </small>
                        </div>

                        <Button
                          variant="light-solid"
                          size="sm"
                          onClick={() => handleMobileDayChange("next")}
                          disabled={!canNavigateMobileNext}
                        >
                          <ChevronRight/>
                        </Button>
                      </div>

                      <div className="w-100 px-0 mt-2 d-flex  align-items-center justify-content-center" 
                              style={{ background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                  height: "20px",
                              }}></div>
                    </Card.Header>

                    <Card.Body className="p-4">
                      {(() => {
                        const currentDay = weekDates[mobileCurrentDayIndex];
                        const dayString = currentDay.format("YYYY-MM-DD");
                        const entry = logData[dayString] || {};
                        const blockedType = isBlockedDay(dayString);
                        const isToday = currentDay.isSame(moment(), "day");
                        const isFuture = isFutureDay(dayString);

                        const amMins =
                          entry.amStart && entry.amEnd
                            ? moment(entry.amEnd, "HH:mm").diff(
                                moment(entry.amStart, "HH:mm"),
                                "minutes"
                              )
                            : 0;
                        const pmMins =
                          entry.pmStart && entry.pmEnd
                            ? moment(entry.pmEnd, "HH:mm").diff(
                                moment(entry.pmStart, "HH:mm"),
                                "minutes"
                              )
                            : 0;
                        // Subtract break time (stored in minutes)
                        const breakMins = entry.breakTime ? parseFloat(entry.breakTime) || 0 : 0;
                        const totalDayMinutes = Math.max(0, amMins + pmMins - breakMins);
                        const totalDay = `${String(
                          Math.floor(totalDayMinutes / 60)
                        ).padStart(2, "0")}:${String(
                          totalDayMinutes % 60
                        ).padStart(2, "0")}`;

                        return (
                          <div>
                            {/* Day Info */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <h5 className="mb-0 fw-semibold">
                                  {currentDay.format("dddd")}
                                </h5>
                                <p className="text-muted mb-0 small">
                                  {currentDay.format("MMMM DD, YYYY")}
                                </p>
                              </div>
                              {isToday && (
                                <span className="badge bg-primary d-flex flex-row align-items-center">
                                  <i
                                    className="bi bi-circle-fill me-1"
                                    style={{ fontSize: ".5rem" }}
                                  ></i>
                                  Today
                                </span>
                              )}
                            </div>

                            {/* Blocked Day Display */}
                            {blockedType && (
                              <div className="text-center mb-4">
                                <div
                                  className={`badge ${
                                    blockedType === "Sick"
                                      ? "bg-info"
                                      : "bg-primary"
                                  } text-white p-3 fs-6`}
                                >
                                  {blockedType} Leave
                                </div>
                                <p className="text-muted mt-2 mb-0">
                                  No work hours to log for this day
                                </p>
                              </div>
                            )}

                            {/* Work Hours Input */}
                            {!blockedType && (
                              <div>
                                {useSlider ? (
                                  /* Time Slider View */
                                  <div>
                                    <div
                                      style={{
                                        ...sliderContainerStyle,
                                        opacity:
                                          isFuture ||
                                          (isCurrentWeekInSubmittedMonth &&
                                            !isDisplayedMonthUnlocked) ||
                                          (isSubmittedWeek &&
                                            !isUnlockedWeek &&
                                            !isDisplayedMonthUnlocked)
                                            ? 0.5
                                            : 1,
                                        pointerEvents:
                                          isFuture ||
                                          (isCurrentWeekInSubmittedMonth &&
                                            !isDisplayedMonthUnlocked) ||
                                          (isSubmittedWeek &&
                                            !isUnlockedWeek &&
                                            !isDisplayedMonthUnlocked)
                                            ? "none"
                                            : "auto",
                                      }}
                                    >
                                      <div
                                        style={{
                                          ...sliderTrackStyle,
                                          opacity:
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? 0.5
                                              : 1,
                                          pointerEvents:
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? "none"
                                              : "auto",
                                        }}
                                      >
                                        <div style={tickMarksStyle}>
                                          {generateTickMarks()}
                                        </div>
                                        <div
                                          style={progressBarStyle(
                                            (convertTimeToMinutes(
                                              entry.amStart ||
                                                getDefaultTimePresets().amStart
                                            ) /
                                              1440) *
                                              100,
                                            (convertTimeToMinutes(
                                              entry.amEnd ||
                                                getDefaultTimePresets().amEnd
                                            ) /
                                              1440) *
                                              100,
                                            sliderInteraction[dayString],
                                            hasTimeData(entry)
                                          )}
                                        />
                                        <div
                                          style={{
                                            ...sliderThumbStyle(
                                              (convertTimeToMinutes(
                                                entry.amStart || "06:00"
                                              ) /
                                                1440) *
                                                100
                                            ),
                                            opacity:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? 0.5
                                                : 1,
                                            pointerEvents:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? "none"
                                                : "auto",
                                          }}
                                          onMouseDown={
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? undefined
                                              : handleSliderMouseDown(
                                                  dayString,
                                                  "start"
                                                )
                                          }
                                          onTouchStart={
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? undefined
                                              : handleSliderTouchStart(
                                                  dayString,
                                                  "start"
                                                )
                                          }
                                        />
                                        <div
                                          style={{
                                            ...sliderThumbStyle(
                                              (convertTimeToMinutes(
                                                entry.amEnd || "18:00"
                                              ) /
                                                1440) *
                                                100
                                            ),
                                            opacity:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? 0.5
                                                : 1,
                                            pointerEvents:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? "none"
                                                : "auto",
                                          }}
                                          onMouseDown={
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? undefined
                                              : handleSliderMouseDown(
                                                  dayString,
                                                  "end"
                                                )
                                          }
                                          onTouchStart={
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked)
                                              ? undefined
                                              : handleSliderTouchStart(
                                                  dayString,
                                                  "end"
                                                )
                                          }
                                        />
                                      </div>
                                      <div
                                        style={{
                                          ...timeDisplayStyle,
                                          opacity:
                                            isCurrentWeekInSubmittedMonth &&
                                            !isDisplayedMonthUnlocked
                                              ? 0.5
                                              : 1,
                                        }}
                                      >
                                        <div className="d-flex flex-row align-items-center gap-2 mb-2">
                                          <span className="text-muted small">
                                            Start Time
                                          </span>
                                          <span className="fw-semibold fs-6">
                                            {entry.amStart || ""}
                                          </span>
                                        </div>
                                        <div className="d-flex flex-row align-items-center gap-2">
                                          <span className="text-muted small">
                                            End Time
                                          </span>
                                          <span className="fw-semibold fs-6">
                                            {entry.amEnd || ""}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  /* Time Input View */
                                  <div>
                                    <div className="row mb-4">
                                      <div className="col-6">
                                        <FloatingLabel
                                          label="Start Time"
                                          controlId="mobileStartTime"
                                        >
                                          <Form.Control
                                            type="time"
                                            value={entry.amStart || ""}
                                            onChange={(e) =>
                                              handleTimeChange(
                                                dayString,
                                                "amStart",
                                                e.target.value
                                              )
                                            }
                                            disabled={isDayLoggingDisabled(dayString)}
                                          />
                                        </FloatingLabel>
                                      </div>
                                      <div className="col-6">
                                        <FloatingLabel
                                          label="End Time"
                                          controlId="mobileEndTime"
                                        >
                                          <Form.Control
                                            type="time"
                                            value={entry.amEnd || ""}
                                            onChange={(e) =>
                                              handleTimeChange(
                                                dayString,
                                                "amEnd",
                                                e.target.value
                                              )
                                            }
                                            disabled={isDayLoggingDisabled(dayString)}
                                          />
                                        </FloatingLabel>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Break Duration */}
                                <div className="mb-3">
                                  <FloatingLabel
                                    label="Break Duration (minutes)"
                                    controlId="mobileBreakMinutes"
                                  >
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={
                                        breakMins > 0
                                          ? breakMins.toString()
                                          : ""
                                      }
                                      disabled={isDayLoggingDisabled(dayString)}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          dayString,
                                          "breakTime",
                                          e.target.value
                                            ? (
                                                parseFloat(e.target.value) ||
                                                0
                                              ).toString()
                                            : ""
                                        )
                                      }
                                    />
                                  </FloatingLabel>
                                </div>

                                {/* Total Hours */}
                                <div className="text-center mb-4">
                                  <div className="bg-light p-3 rounded">
                                    <h6 className="text-muted mb-1">
                                      Total Hours
                                    </h6>
                                    <h3 className="fw-bold mb-0">{totalDay}</h3>
                                  </div>
                                </div>

                                {/* Comments */}
                                <div className="mb-3">
                                  <FloatingLabel
                                    label="Comments (optional)"
                                    controlId="mobileComments"
                                  >
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      placeholder="Add any notes or comments about this day..."
                                      value={sidebarComments[dayString] || ""}
                                      disabled={isDayLoggingDisabled(dayString)}
                                      onChange={(e) =>
                                        setSidebarComments((prev) => ({
                                          ...prev,
                                          [dayString]: e.target.value,
                                        }))
                                      }
                                    />
                                  </FloatingLabel>
                                </div>

                                {/* Save Button */}
                                <Button
                                  variant="primary"
                                  className="w-100"
                                  size="sm"
                                  onClick={handleMobileSave}
                                  disabled={
                                    mobileSaving || isDayLoggingDisabled(dayString)
                                  }
                                >
                                  {mobileSaving ? (
                                    <>
                                      <div className="d-flex align-items-center gap-2">
                                      <Hourglass />
                                      Saving...
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      Save Changes
                                    </>
                                  )}
                                </Button>
                                {/* Submit / Unlock buttons for mobile view */}
                                {(!isSubmittedWeek ||
                                  isUnlockedWeek ||
                                  isDisplayedMonthUnlocked) &&
                                  (!isDisplayedMonthFinalized ||
                                    isDisplayedMonthUnlocked) &&
                                  isLastWeekOfMonth() &&
                                  !isCurrentWeekCrossingMonths() && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      className="w-100 mt-2"
                                      onClick={() =>
                                        setShowConfirmationModal(true)
                                      }
                                    >
                                      Submit For Approval
                                    </Button>
                                  )}
                                {isDisplayedMonthFinalized &&
                                  !isDisplayedMonthUnlocked && (
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      className="w-100 mt-2"
                                      onClick={handleRequestUnlock}
                                    >
                                      Request Unlock
                                    </Button>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </Card.Body>
                  </Card>
              </div>
            )}
            {/* Desktop View */}
              <div className="d-none d-md-block">
                <Row>
                  <Col lg={9}>
                    <Card className="border-0 shadow-sm mb-3">
                      <Card.Header
                        className="p-4 border-0 d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: "var(--bs-body-bg)" }}
                      >
                            <div className="d-flex gap-3 align-items-center justify-content-start">
                              <span className="h-100 h5 mb-0 bg-secondary rounded-3 px-3 py-2 text-muted d-flex align-items-center justify-content-center"><i className="bi bi-calendar-date py-1"></i></span>
                              <div className="d-flex flex-column align-items-start justify-content-start">
                              <h5 className="fw-bold mb-0">Week {moment().add(weekOffset, "weeks").week()} - {moment().add(weekOffset, "weeks").format("MMMM YYYY")}</h5>
                              <p className="small text-muted mb-0">
                                {moment().add(weekOffset, "weeks").startOf("week").format("MMM D")} - {moment().add(weekOffset, "weeks").endOf("week").format("MMM D, YYYY")}
                              </p>
                              </div>
                              {(isSubmittedWeek || isMonthFinalized) &&
                                !isUnlockedWeek &&
                                !isDisplayedMonthUnlocked && (
                                  <span className="badge bg-success">Finalized</span>
                                )}
                              {(isUnlockedWeek || isDisplayedMonthUnlocked) && (
                                <span className="badge bg-warning">
                                  <i className="bi bi-unlock me-2"></i>Editable
                                </span>
                              )}
                            </div>
                          
                          <div className="d-flex gap-2">
                            {/* This Week Button */}
                            {weekOffset !== 0 && (
                              <Button
                              size="sm"
                                onClick={() => setWeekOffset(0)}
                                variant="light-solid"
                              >
                                <i className="bi bi-calendar-date me-2"></i>
                                Current Week
                              </Button>
                            )} 
                            <Button
                              size="sm"
                              onClick={() => setWeekOffset(weekOffset - 1)}
                              variant="light-solid"
                            >
                              <i className="bi bi-chevron-left"></i>
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setWeekOffset(weekOffset + 1)}
                              variant="light-solid"
                              disabled={weekOffset >= 0}
                            >
                              <i className="bi bi-chevron-right"></i>
                            </Button>
                            </div>
                    
                      </Card.Header>
                      <Card.Body className="p-0">
                        {useWeekGrid ? (
                          <>
                            <WeekHourGrid
                              weekDates={weekDates}
                              logData={logData}
                              isDayDisabled={isDayLoggingDisabled}
                              blockedTypeFor={isBlockedDay}
                              onCommit={commitWeekGridSelection}
                            />
                            <div className="border-top text-muted small text-center py-2">
                              <span>drag to add/edit selection, double click to remove selection</span>
                            </div>
                          </>
                        ) : (
                  
                        <>
                        {/* Hours Table */}

                        <Table responsive className="mb-0 border-top">
                          <thead>
                            <tr className="py-4">
                              <th className="ps-4 bg-light">Date</th>
                              {/* <th className="bg-light">Rotation</th> */}
                              <th style={{ width: "50%" }} className="bg-light">
                                Clocked Hours
                              </th>
                              <th className="bg-light">Break (hrs)</th>
                              <th className="bg-light">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {weekDates.map((day, i) => {
                              const d = day.format("YYYY-MM-DD");
                              const entry = logData[d] || {};
                              const blockedType = isBlockedDay(d);
                              const amMins =
                                entry.amStart && entry.amEnd
                                  ? moment(entry.amEnd, "HH:mm").diff(
                                      moment(entry.amStart, "HH:mm"),
                                      "minutes"
                                    )
                                  : 0;
                              const pmMins =
                                entry.pmStart && entry.pmEnd
                                  ? moment(entry.pmEnd, "HH:mm").diff(
                                      moment(entry.pmStart, "HH:mm"),
                                      "minutes"
                                    )
                                  : 0;
                              // Subtract break time (stored in minutes)
                              const breakMins = entry.breakTime ? parseFloat(entry.breakTime) || 0 : 0;
                              const totalDayMinutes = Math.max(0, amMins + pmMins - breakMins);
                              const totalDay = `${String(
                                Math.floor(totalDayMinutes / 60)
                              ).padStart(2, "0")}:${String(
                                totalDayMinutes % 60
                              ).padStart(2, "0")}`;
                              const isToday = day.isSame(moment(), "day");
                              const isFuture = isFutureDay(d);
                              const monthShort = day.format("MMM");
                              const dayNum = day.format("D");
                              const isSelected = selectedWeekDateForDisplay === d;

                              return (
                                <tr
                                  key={i}
                                  className={`${isToday ? "table-active" : ""} ${
                                    isSelected ? "table-row-selected" : ""
                                  }`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setSelectedWeekDate(d)}
                                >
                                  <td className="text-start ps-4">
                                    <div className="rounded-3 d-flex flex-column border" style={{ width: "60px", height: "50px" }}>
                                      <div className={`d-flex flex-row align-items-center pt-1 px-3 justify-content-center rounded-top ${isToday ? 'bg-primary-solid' : 'bg-secondary'} `}>
                                        <span className={`text-uppercase ${isToday ? 'text-white' : 'text-muted'}`} style={{ fontSize: '0.65rem' }}>
                                        {day.format("ddd")}
                                        </span>
                                      </div>
                                      <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-lighter rounded-bottom" >
                                        <h6 className="mb-0 fw-bold">{dayNum}</h6> </div>                                        
                                      </div>
                                    {/*<span className="d-flex align-items-center gap-2">
                                      {formatDateLabel(day)}
                                      {isToday && (
                                        <span className="text-muted small badge bg-primary align-items-center d-flex flex-row gap-1">
                                          Today
                                        </span>
                                      )}
                                    </span>*/}
                                  </td>
                                  {/* Rotation Column */}
                                  {/* <td className="text-center">
                                    {blockedType ? (
                                      <span>--</span>
                                    ) : (
                                      <Form.Control
                                        type="text"
                                        placeholder="Rotation"
                                        value={entry.rotation || ""}
                                        onChange={(e) =>
                                          handleTimeChange(
                                            d,
                                            "rotation",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          (isCurrentWeekInSubmittedMonth &&
                                            !isDisplayedMonthUnlocked) ||
                                          (isSubmittedWeek &&
                                            !isUnlockedWeek &&
                                            !isDisplayedMonthUnlocked) ||
                                          blockedType !== null
                                        }
                                        style={{ minWidth: "120px" }}
                                      />
                                    )}
                                  </td>
                                  {/* Clocked Hours Column */}
                                  <td className="text-center"> 
                                    {blockedType ? (
                                      <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{ height: "40px" }}
                                      >
                                        <span
                                          className={`badge ${
                                            blockedType === "Sick"
                                              ? "bg-info"
                                              : "bg-primary"
                                          } text-white`}
                                        >
                                          {blockedType} Leave
                                        </span>
                                      </div>
                                    ) : (
                                      <div
                                        className="px-3"
                                        style={{
                                          ...sliderContainerStyle,
                                          opacity:
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked) ||
                                            (isSubmittedWeek &&
                                              !isUnlockedWeek &&
                                              !isDisplayedMonthUnlocked)
                                              ? 0.5
                                              : 1,
                                          pointerEvents:
                                            isFuture ||
                                            (isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked) ||
                                            (isSubmittedWeek &&
                                              !isUnlockedWeek &&
                                              !isDisplayedMonthUnlocked)
                                              ? "none"
                                              : "auto",
                                        }}
                                      >
                                        <div
                                          style={{
                                            ...sliderTrackStyle,
                                            opacity:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? 0.5
                                                : 1,
                                            pointerEvents:
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? "none"
                                                : "auto",
                                          }}
                                        >
                                          <div style={tickMarksStyle}>
                                            {generateTickMarks()}
                                          </div>
                                          <div
                                            style={progressBarStyle(
                                              (convertTimeToMinutes(
                                                entry.amStart ||
                                                  getDefaultTimePresets().amStart
                                              ) /
                                                1440) *
                                                100,
                                              (convertTimeToMinutes(
                                                entry.amEnd ||
                                                  getDefaultTimePresets().amEnd
                                              ) /
                                                1440) *
                                                100,
                                              sliderInteraction[d],
                                              hasTimeData(entry)
                                            )}
                                          />
                                          <div
                                            style={{
                                              ...sliderThumbStyle(
                                                (convertTimeToMinutes(
                                                  entry.amStart || "06:00"
                                                ) /
                                                  1440) *
                                                  100
                                              ),
                                              opacity:
                                                isFuture ||
                                                (isCurrentWeekInSubmittedMonth &&
                                                  !isDisplayedMonthUnlocked)
                                                  ? 0.5
                                                  : 1,
                                              pointerEvents:
                                                isFuture ||
                                                (isCurrentWeekInSubmittedMonth &&
                                                  !isDisplayedMonthUnlocked)
                                                  ? "none"
                                                  : "auto",
                                            }}
                                            onMouseDown={
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? undefined
                                                : handleSliderMouseDown(d, "start")
                                            }
                                            onTouchStart={
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? undefined
                                                : handleSliderTouchStart(d, "start")
                                            }
                                          />
                                          <div
                                            style={{
                                              ...sliderThumbStyle(
                                                (convertTimeToMinutes(
                                                  entry.amEnd || "18:00"
                                                ) /
                                                  1440) *
                                                  100
                                              ),
                                              opacity:
                                                isFuture ||
                                                (isCurrentWeekInSubmittedMonth &&
                                                  !isDisplayedMonthUnlocked)
                                                  ? 0.5
                                                  : 1,
                                              pointerEvents:
                                                isFuture ||
                                                (isCurrentWeekInSubmittedMonth &&
                                                  !isDisplayedMonthUnlocked)
                                                  ? "none"
                                                  : "auto",
                                            }}
                                            onMouseDown={
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? undefined
                                                : handleSliderMouseDown(d, "end")
                                            }
                                            onTouchStart={
                                              isFuture ||
                                              (isCurrentWeekInSubmittedMonth &&
                                                !isDisplayedMonthUnlocked)
                                                ? undefined
                                                : handleSliderTouchStart(d, "end")
                                            }
                                          />
                                        </div>
                                        <div
                                          style={{
                                            ...timeDisplayStyle,
                                            opacity:
                                              isCurrentWeekInSubmittedMonth &&
                                              !isDisplayedMonthUnlocked
                                                ? 0.5
                                                : 1,
                                          }}
                                        >
                                          <div className="d-flex flex-row align-items-center gap-2">
                                            <span className="text-muted small">
                                              Start Time
                                            </span>
                                            <span className="fw-semibold fs-6">
                                              {entry.amStart ||
                                                (hasTimeData(entry)
                                                  ? getDefaultTimePresets().amStart
                                                  : "")}
                                            </span>
                                          </div>
                                          <div className="d-flex flex-row align-items-center gap-2">
                                            <span className="text-muted small">
                                              End Time
                                            </span>
                                            <span className="fw-semibold fs-6">
                                              {entry.amEnd ||
                                                (hasTimeData(entry)
                                                  ? getDefaultTimePresets().amEnd
                                                  : "")}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  {/* Break Column */}
                                  <td className="text-center" style={{ maxWidth: "100px" }}>
                                    <span>{entry.breakTime ? (parseFloat(entry.breakTime) / 60).toFixed(1) : "-"}</span>
                           
                                   {/* {blockedType ? (
                                      <span>--</span>
                                    ) : (
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        placeholder="0.0"
                                        value={entry.breakTime ? (parseFloat(entry.breakTime) / 60).toFixed(1) : ""}
                                        onChange={(e) =>
                                          handleTimeChange(
                                            d,
                                            "breakTime",
                                            e.target.value ? (parseFloat(e.target.value) * 60).toString() : ""
                                          )
                                        }
                                        disabled={
                                          (isCurrentWeekInSubmittedMonth &&
                                            !isDisplayedMonthUnlocked) ||
                                          (isSubmittedWeek &&
                                            !isUnlockedWeek &&
                                            !isDisplayedMonthUnlocked) ||
                                          blockedType !== null
                                        }
                                        style={{ minWidth: "80px" }}
                                      />
                                    )} */}
                                  </td>
                                  {/* Total Column */}
                                  <td className=" text-center">
                                    <span className="fs-6 fw-semibold">
                                      {blockedType ? <span>--</span> : totalDay}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <div className="mt-3 d-flex justify-content-end">
                          {editedDaysInWeek > 0 &&
                            (isEditableWeek || isDisplayedMonthUnlocked) && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={handleSave}
                                className="mb-2"
                              >
                                Save Weekly Hrs
                              </Button>
                            )}
                        </div>
                        </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={3}>
                      {useWeekGrid ? (
                        <div className="custom-sticky-top" style={{ top: "80px" }}>
                          <WeekHourOverview
                            weekDates={weekDates}
                            logData={logData}
                            comments={sidebarComments}
                            onCommentChange={handleSidebarCommentChange}
                            onTimeChange={handleTimeChange}
                            onWeekCategoryChange={handleWeekCategoryChange}
                            isDayDisabled={isDayLoggingDisabled}
                            blockedTypeFor={isBlockedDay}
                            minutesForEntry={calculateTotalMinutes}
                            rotationOptions={rotationOptions}
                            weekTotal={weekTotal}
                            onSave={handleSaveWeekGrid}
                            saving={savingWeek}
                            saveDisabled={
                              savingWeek ||
                              !(isEditableWeek || isDisplayedMonthUnlocked)
                            }
                            locked={
                              (isSubmittedWeek || isMonthFinalized) &&
                              !isUnlockedWeek &&
                              !isDisplayedMonthUnlocked
                            }
                            extraActions={
                              <>
                                {(!isSubmittedWeek ||
                                  isUnlockedWeek ||
                                  isDisplayedMonthUnlocked) &&
                                  (!isDisplayedMonthFinalized ||
                                    isDisplayedMonthUnlocked) &&
                                  isLastWeekOfMonth() &&
                                  !isCurrentWeekCrossingMonths() && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() =>
                                        setShowConfirmationModal(true)
                                      }
                                    >
                                      Submit For Approval
                                    </Button>
                                  )}
                                {isDisplayedMonthFinalized &&
                                  !isDisplayedMonthUnlocked && (
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={handleRequestUnlock}
                                    >
                                      Request Unlock
                                    </Button>
                                  )}
                              </>
                            }
                          />
                        </div>
                      ) : (
                      <>
                      {/* Right Sidebar */}
                      <div className="custom-sticky-top" style={{ top: "80px" }}>
                          <Card className="border-0 shadow-sm mb-3">
                            <Card.Header
                              className="border-0 text-white px-0"
                              style={{
                                background:
                                  "linear-gradient(45deg, var(--blue) 0%, #252F4A 60%, var(--green) 100%)",
                                  height: "70px",
                              }}
                            >
                            
                              <div className="d-flex align-items-center gap-2 px-3">
                                <div>
                                  <div className="smaller text-white-75">
                                    Selected Day
                                  </div>
                                  <div className="fw-semibold">
                                    {moment(selectedWeekDate).format(
                                      "dddd, MMM D"
                                    )}
                                  </div>
                                </div>
                              </div> 
                              <div className="w-100 px-0 mt-2 d-flex  align-items-center justify-content-center" 
                              style={{ background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                  height: "12px",
                              }}></div>
                            </Card.Header>
                            <Card.Body className="p-3">
                              {(() => {
                                const entry =
                                  logData[selectedWeekDateForDisplay] || {};
                                const blockedType = isBlockedDay(
                                  selectedWeekDateForDisplay
                                );
                                const breakMins = entry.breakTime
                                  ? parseFloat(entry.breakTime) || 0
                                  : 0;
                                const totalMins = calculateTotalMinutes(entry);
                                const totalHours =
                                  totalMins > 0
                                    ? (totalMins / 60).toFixed(1)
                                    : "0.0";

                                const isDisabled = isDayLoggingDisabled(
                                  selectedWeekDateForDisplay
                                );

                                return (
                                  <>
                                    <div className="mb-3">
                                      <Row className="g-2">
                                        <Col xs={6}>
                                          <FloatingLabel
                                            label="Start Time"
                                            controlId="sidebarStartTime"
                                          >
                                            <Form.Control
                                              type="time"
                                              value={entry.amStart || ""}
                                              disabled={isDisabled}
                                              onChange={(e) =>
                                                handleTimeChange(
                                                  selectedWeekDateForDisplay,
                                                  "amStart",
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FloatingLabel>
                                        </Col>
                                        <Col xs={6}>
                                          <FloatingLabel
                                            label="End Time"
                                            controlId="sidebarEndTime"
                                          >
                                            <Form.Control
                                              type="time"
                                              value={entry.amEnd || ""}
                                              disabled={isDisabled}
                                              onChange={(e) =>
                                                handleTimeChange(
                                                  selectedWeekDateForDisplay,
                                                  "amEnd",
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </FloatingLabel>
                                        </Col>
                                      </Row>
                                    </div>

                                    <div className="mb-3">
                                      <FloatingLabel
                                        label="Break Duration (minutes)"
                                        controlId="sidebarBreak"
                                      >
                                        <Form.Control
                                          type="number"
                                          min="0"
                                          step="1"
                                          value={
                                            breakMins > 0
                                              ? breakMins.toString()
                                              : ""
                                          }
                                          disabled={isDisabled}
                                          onChange={(e) =>
                                            handleTimeChange(
                                              selectedWeekDateForDisplay,
                                              "breakTime",
                                              e.target.value
                                                ? (
                                                    parseFloat(
                                                      e.target.value
                                                    ) || 0
                                                  ).toString()
                                                : ""
                                            )
                                          }
                                        />
                                      </FloatingLabel>
                                    </div>

                                    <div className="mb-3">
                                      <div className="bg-light rounded-3 p-3">
                                        <div className="text-muted small mb-1">
                                          Total Work Hours
                                        </div>
                                        <div className="d-flex align-items-end gap-2">
                                          <span className="fw-bold display-6">
                                            {totalHours}
                                          </span>
                                          <span className="text-muted small mb-2">
                                            hours
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="mb-3">
                                      <FloatingLabel
                                        label="Comments (optional)"
                                        controlId="sidebarComments"
                                      >
                                        <Form.Control
                                          as="textarea"
                                          rows={3}
                                          placeholder="Add any notes or comments about this day..."
                                          value={
                                            sidebarComments[
                                              selectedWeekDateForDisplay
                                            ] || ""
                                          }
                                          style={{ minHeight: "80px" }}
                                          disabled={isDisabled}
                                          onChange={(e) =>
                                            setSidebarComments((prev) => ({
                                              ...prev,
                                              [selectedWeekDateForDisplay]:
                                                e.target.value,
                                            }))
                                          }
                                        />
                                      </FloatingLabel>
                                    </div>

                                    <Button
                                      variant="primary"
                                      className="w-100"
                                      size="sm"
                                      disabled={isDisabled || sidebarSaving}
                                      onClick={() =>
                                        handleSidebarDaySave(
                                          selectedWeekDateForDisplay
                                        )
                                      }
                                    >
                                      {sidebarSaving ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          Saving...
                                        </>
                                      ) : (
                                        <>
                                          Save Changes
                                        </>
                                      )}
                                    </Button>

                                    {(!isSubmittedWeek ||
                                      isUnlockedWeek ||
                                      isDisplayedMonthUnlocked) &&
                                      (!isDisplayedMonthFinalized ||
                                        isDisplayedMonthUnlocked) &&
                                      isLastWeekOfMonth() &&
                                      !isCurrentWeekCrossingMonths() && (
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          className="w-100 mt-2"
                                          onClick={() =>
                                            setShowConfirmationModal(true)
                                          }
                                        >
                                          Submit For Approval
                                        </Button>
                                      )}
                                    {isDisplayedMonthFinalized &&
                                      !isDisplayedMonthUnlocked && (
                                        <Button
                                          variant="outline-warning"
                                          size="sm"
                                          className="w-100 mt-2"
                                          onClick={handleRequestUnlock}
                                        >
                                          Request Unlock
                                        </Button>
                                      )}
                                  </>
                                );
                              })()}

                            {(isSubmittedWeek || isMonthFinalized) &&
                            !isUnlockedWeek &&
                            !isDisplayedMonthUnlocked && (
                              <div className="alert border-0 alert-success rounded-3 mb-0 mt-3">
                                <div className="fw-semibold mb-1">
                                  Timesheet Submitted
                                </div>
                                <div className="small text-muted">
                                  Your timesheet has been submitted. If you
                                  need to make corrections, request an unlock
                                  for further modifications.
                                </div>
                              </div>
                            )}
                            </Card.Body>
                          </Card>
                        {/* Weekly save / submission actions (no summary card) */}



                      </div>
                      </>
                      )}
                  </Col>
                </Row>

           
              </div>

            {/* Success Modal */}
            <Modal
              show={showSuccessModal}
              onHide={() => setShowSuccessModal(false)}
            >
              <Modal.Header closeButton className="border-bottom-0 px-4">
                <div className="d-flex flex-column justify-content-start align-items-start w-100">
                  <Modal.Title>Success</Modal.Title>
                  <div className="text-hl-success"></div>
                </div>
              </Modal.Header>
              <Modal.Body className="px-4">
                <p>{successMessage}</p>
              </Modal.Body>
              <Modal.Footer className="border-top-0 px-4">
                <Button
                  variant="primary"
                  onClick={() => setShowSuccessModal(false)}
                >
                  OK
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal
              show={showConfirmationModal}
              onHide={() => setShowConfirmationModal(false)}
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Submission</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  Are you sure you want to submit this month's hours? This
                  action cannot be undone.
                </p>
                <p className="fw-bold mt-3">
                  {" "}
                  {/* Use Bootstrap's fw-bold and mt-3 for styling */}
                  Month Total: {monthTotal}
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmationModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSubmit}>
                  Confirm Submission
                </Button>
              </Modal.Footer>
            </Modal>
          </Tab.Pane>

          <Tab.Pane eventKey="vacation" active={activeTab === "vacation"}>
            <Container>
              <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                {exportSuccess && (
                  <div className="alert alert-success mb-0 ms-3 w-100">
                    Your Vacation & Sick Leave requests have been exported to
                    your calendar!
                  </div>
                )}
              </div>
              <Card className="mb-3 shadow-sm border-0">
                <Card.Body>
                  <Row className="p-3">
                    {/* Vacation Semi-Circle Chart */}
                    <Col
                      lg={6}
                      md={6}
                      className="d-flex flex-column align-items-center mb-4"
                    >
                      <h6 className="fw-semibold mb-3 text-primary">
                        Vacation Days
                      </h6>
                      <div className="d-flex flex-row align-items-center justify-content-between gap-4">
                        <div className="d-flex flex-column align-items-center">
                          <div
                            style={{
                              width: "180px",
                              height: "90px",
                              position: "relative",
                            }}
                          >
                            <svg width="180" height="110" viewBox="0 0 180 90">
                              {/* Background Semi-Circle */}
                              <path
                                d="M 20 70 A 50 50 0 0 1 160 70"
                                fill="none"
                                stroke="var(--gray-300)"
                                strokeWidth="12"
                                strokeLinecap="round"
                              />
                              {/* Used Vacation Days */}
                              <path
                                d="M 20 70 A 50 50 0 0 1 160 70"
                                fill="none"
                                stroke="var(--blue)"
                                strokeWidth="12"
                                strokeLinecap="round"
                                strokeDasharray={`${
                                  (leaveAllotment.usedVacationDays /
                                    leaveAllotment.vacationDays) *
                                  219.91
                                } 219.91`}
                              />
                            </svg>
                            {/* Center Text */}
                            <div
                              style={{
                                position: "absolute",
                                top: "70%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "bold",
                                  color: "var(--blue)",
                                }}
                              >
                                {leaveAllotment.usedVacationDays}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--gray-600)",
                                }}
                              >
                                of {leaveAllotment.vacationDays} days
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Vacation Labels */}
                        <div className="ms-4">
                          <div className="d-flex align-items-center mb-2">
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "var(--blue)",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ fontSize: "14px" }}
                              >
                                Used
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                {leaveAllotment.usedVacationDays} days
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "var(--gray-300)",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ fontSize: "14px" }}
                              >
                                Remaining
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                {leaveAllotment.vacationDays -
                                  leaveAllotment.usedVacationDays}{" "}
                                days
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>

                    {/* Sick Leave Semi-Circle Chart */}
                    <Col
                      lg={6}
                      md={6}
                      className="d-flex flex-column align-items-center mb-4"
                    >
                      <h6 className="fw-semibold mb-3 text-info">
                        Sick Leave Days
                      </h6>
                      <div className="d-flex flex-row align-items-center justify-content-between gap-4">
                        <div className="d-flex flex-column align-items-center">
                          <div
                            style={{
                              width: "180px",
                              height: "90px",
                              position: "relative",
                            }}
                          >
                            <svg width="180" height="110" viewBox="0 0 180 90">
                              {/* Background Semi-Circle */}
                              <path
                                d="M 20 70 A 50 50 0 0 1 160 70"
                                fill="none"
                                stroke="var(--gray-300)"
                                strokeWidth="12"
                                strokeLinecap="round"
                              />
                              {/* Used Sick Leave Days */}
                              <path
                                d="M 20 70 A 50 50 0 0 1 160 70"
                                fill="none"
                                stroke="var(--indigo)"
                                strokeWidth="12"
                                strokeLinecap="round"
                                strokeDasharray={`${
                                  (leaveAllotment.usedSickDays /
                                    leaveAllotment.sickDays) *
                                  219.91
                                } 219.91`}
                              />
                            </svg>
                            {/* Center Text */}
                            <div
                              style={{
                                position: "absolute",
                                top: "70%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "bold",
                                  color: "var(--indigo)",
                                }}
                              >
                                {leaveAllotment.usedSickDays}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--gray-600)",
                                }}
                              >
                                of {leaveAllotment.sickDays} days
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Sick Leave Labels */}
                        <div className="ms-4">
                          <div className="d-flex align-items-center mb-2">
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "var(--indigo)",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ fontSize: "14px" }}
                              >
                                Used
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                {leaveAllotment.usedSickDays} days
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "var(--gray-300)",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <div>
                              <div
                                className="fw-semibold"
                                style={{ fontSize: "14px" }}
                              >
                                Remaining
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                {leaveAllotment.sickDays -
                                  leaveAllotment.usedSickDays}{" "}
                                days
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className="shadow-sm border-0">
                <Card.Header
                  className="border-0 p-4 PY-3 d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex flex-column">
                    <h5 className="fw-semibold mb-0">Vacation Requests</h5>
                    <p className="text-muted small mb-0">showing {vacationRequests.length} requests</p>
                  </div>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-end">
                    <Form.Select
                      value={selectedAcademicYear}
                      onChange={(e) => handleAcademicYearChange(e.target.value)}
                      size="sm"
                    >
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() - 2 + i;
                        return (
                          <option key={year} value={`${year}-${year + 1}`}>
                            {year}-{year + 1}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover>
                    <thead>
                      <tr className="custom-header-bg">
                        <th className="px-4">Type</th>
                        <th>Date Range</th>
                        <th>Days</th>
                        <th>Status</th>
                        <th>Reason</th>
                        <th>Denial Reason</th>
                        <th>Requested</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vacationRequests.length === 0 ? (
                        <tr>
                          <td colSpan={7}>
                            <div className="text-center py-5">
                              <div className="mb-4">
                                <img
                                  src="/images/undraw_travel-mode_ydxo.svg"
                                  style={{ width: "300px" }}
                                  alt="No vacation requests"
                                />
                              </div>
                              <div className="text-muted">
                                <h5 className="mb-2">
                                  No vacation requests found
                                </h5>
                                <p className="mb-0 fw-normal">
                                  You haven't submitted any vacation or sick
                                  leave requests yet
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        vacationRequests.map((r) => {
                          const daysBreakdown = calculateWorkdaysAndWeekends(r.startDate, r.endDate);
                          return (
                            <tr key={r.id}>
                              <td className="px-4">{r.type}</td>
                              <td>
                                {moment(r.startDate).format("YYYY-MM-DD")} to{" "}
                                {moment(r.endDate).format("YYYY-MM-DD")}
                              </td>
                              <td>
                                <span className="fw-medium">{daysBreakdown}</span>
                              </td>
                              <td>
                                <span
                                  className={`badge rounded-2 ${
                                    r.status === "Approved"
                                      ? "bg-success"
                                      : r.status === "Denied"
                                      ? "bg-danger"
                                      : "bg-warning"
                                  }`}
                                >
                                  {r.status}
                                </span>
                              </td>
                              <td>{r.reason}</td>
                              <td>{r.status === "Denied" ? r.reason : ""}</td>
                              <td>{moment(r.createdAt).format("YYYY-MM-DD")}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Container>

            {/* Request Modal */}
            <Modal
              show={showRequestModal}
              onHide={() => setShowRequestModal(false)}
            >
              <Modal.Header className="px-4 border-bottom-0" closeButton>
                <Modal.Title>
                  <div className="d-flex flex-column justify-content-start align-items-start">
                    <span>Request Leave Form</span>
                    <div className="text-hl"></div>
                  </div>
                </Modal.Title>
              </Modal.Header>
              <Form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (isInvalidRequest()) return;
                  setRequestSubmitting(true);
                  setRequestError("");
                  try {
                    const token = localStorage.getItem("token");
                    const [startYear] = selectedAcademicYear.split("-");

                    // Check for overlapping requests in the same academic year
                    const overlappingRequest = vacationRequests.find(
                      (request) => {
                        const existingStart = moment(request.startDate);
                        const existingEnd = moment(request.endDate);
                        const newStart = moment(requestStart);
                        const newEnd = moment(requestEnd);

                        // Don't consider denied requests or requests of different types
                        if (
                          request.status === "Denied" ||
                          request.type !== requestType
                        ) {
                          return false;
                        }

                        // Check for any overlap
                        return (
                          (newStart.isSameOrAfter(existingStart) &&
                            newStart.isBefore(existingEnd)) ||
                          (newEnd.isAfter(existingStart) &&
                            newEnd.isSameOrBefore(existingEnd)) ||
                          (newStart.isSameOrBefore(existingStart) &&
                            newEnd.isSameOrAfter(existingEnd))
                        );
                      }
                    );

                    if (overlappingRequest) {
                      // If there's an overlapping request, ask for confirmation
                      if (
                        !window.confirm(
                          `You already have a ${overlappingRequest.type} request for these dates. ` +
                            `Would you like to update the existing request instead of creating a new one?`
                        )
                      ) {
                        setRequestSubmitting(false);
                        return;
                      }

                      // Update existing request
                      const updateRes = await fetch(
                        `${API_ENDPOINTS.RESIDENT}/vacation-request/${overlappingRequest.id}`,
                        {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            type: requestType,
                            startDate: requestStart,
                            endDate: requestEnd,
                            reason: requestReason,
                            academicYear: selectedAcademicYear,
                            yearStart: `${startYear}-07-01`,
                            yearEnd: `${parseInt(startYear) + 1}-06-30`,
                          }),
                        }
                      );

                      if (!updateRes.ok) {
                        const data = await updateRes.json();
                        throw new Error(
                          data.message || "Failed to update request"
                        );
                      }
                    } else {
                      // Create new request
                      const res = await fetch(
                        `${API_ENDPOINTS.RESIDENT}/vacation-request`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            type: requestType,
                            startDate: requestStart,
                            endDate: requestEnd,
                            reason: requestReason,
                            academicYear: selectedAcademicYear,
                            yearStart: `${startYear}-07-01`,
                            yearEnd: `${parseInt(startYear) + 1}-06-30`,
                          }),
                        }
                      );

                      if (!res.ok) {
                        const data = await res.json();
                        throw new Error(
                          data.message || "Failed to submit request"
                        );
                      }
                    }

                    setShowRequestModal(false);
                    setRequestType("Vacation");
                    setRequestStart("");
                    setRequestEnd("");
                    setRequestReason("");
                    fetchVacationRequests();
                  } catch (err) {
                    setRequestError(err.message || "Failed to submit request");
                  } finally {
                    setRequestSubmitting(false);
                  }
                }}
              >
                <Modal.Body className="px-4 p-0">
                  {requestError && (
                    <div className="alert alert-danger">{requestError}</div>
                  )}
                  {getRequestValidationError() && (
                    <div className="alert alert-warning">
                      {getRequestValidationError()}
                    </div>
                  )}
                  <Row>
                    <Col lg={12}>
                      <Form.Group className="mb-3">
                        <FloatingLabel
                          label="Request Type"
                          controlId="floatingInput"
                        >
                          <Form.Select
                            value={requestType}
                            onChange={(e) => setRequestType(e.target.value)}
                            required
                          >
                            <option value="Vacation">Vacation</option>
                            <option value="Sick">Sick</option>
                          </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel
                          label="Start Date"
                          controlId="floatingInput"
                        >
                          <Form.Control
                            type="date"
                            value={requestStart}
                            onChange={(e) => setRequestStart(e.target.value)}
                            required
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel
                          label="End Date"
                          controlId="floatingInput"
                        >
                          <Form.Control
                            type="date"
                            value={requestEnd}
                            onChange={(e) => setRequestEnd(e.target.value)}
                            required
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col lg={12}>
                      <Form.Group className="mb-3">
                        <FloatingLabel label="Reason" controlId="floatingInput">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={requestReason}
                            onChange={(e) => setRequestReason(e.target.value)}
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer className="px-4 mb-2 border-top-0">
                  <Button
                    variant="light"
                    onClick={() => setShowRequestModal(false)}
                    disabled={requestSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={requestSubmitting || isInvalidRequest()}
                  >
                    Submit
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>
          </Tab.Pane>
        </Tab.Content>
      </Container>
    </div>
  );
};

export default WorkHourLog;
